import i18n from "i18next";
import { initReactI18next } from "react-i18next";

/* ============================================================
 * BlueRoam — Conversion-focused copy + 6 high-quality translations
 * Languages: en (default) · fr · de · es · el · ar · zh · ja
 * (Turkish removed in iter-19.)
 * ============================================================ */

const en = {
  common: {
    brand: "BlueRoam",
    tagline: "Travel light. Stay online.",
    searchPlaceholder: "Where are you going?",
    buy: "Get this plan",
    continue: "Continue",
    back: "Back",
    email: "Email",
    password: "Password",
    name: "Name",
    login: "Log in",
    register: "Create account",
    logout: "Log out",
    dashboard: "My eSIMs",
    admin: "Admin",
    loading: "Loading…",
    error: "Something went wrong",
    price: "Price",
    from: "from",
    data: "Data",
    days: "Days",
    country: "Country",
    plans: "plans",
    cancel: "Cancel",
    save: "Save",
    retry: "Try again",
    status: "Status",
    actions: "Actions",
    language: "Language",
    close: "Close",
    welcome: "Welcome",
    destination: "Destination",
    yourEmail: "Your email",
    payWith: "Pay with card",
    unlimited: "Unlimited",
    moreCountries: "more countries",
    bubbleReady: "eSIM READY",
    bubbleConnected: "Connected in Paris",
    bubbleNetwork: "Orange FR · 5G",
    bubblePlanShort: "+5 GB · 30 days",
    topup: "Renew / Top up",
    topupTitle: "Renew this eSIM",
    topupSub: "Pick a new plan — your data stacks on the same eSIM",
    sales: "Sales",
    export: "Export CSV",
    chartRevenue: "Revenue",
    chartOrders: "Orders",
    last30Days: "Last 30 days",
    topCountries: "Top countries",
    topPlans: "Top plans",
    aov: "Avg. order",
    conversion: "Conversion",
    fulfillment: "Fulfillment rate",
    newCustomers: "New customers",
    topupCount: "Top-ups",
    payfly: "Pay & Fly",
    calculator: "Data Calculator",
  },
  calc: {
    eyebrow: "Data Calculator",
    title: "How much data will you need?",
    pageTitle: "Find your sweet spot",
    pageSub: "Answer 3 quick questions and we'll recommend the right plan size for your trip — no overpaying, no running out.",
    q1: "Where are you going?",
    pickCountry: "Pick a country",
    q2: "How long is your trip?",
    q3: "What will you mostly do?",
    actSocial: "Social & messaging",
    actStream: "Streaming & video",
    actMaps: "Maps & light browsing",
    actWork: "Email & work tools",
    trip1: "1–3 days",
    trip2: "4–6 days",
    trip3: "1–2 weeks",
    trip4: "2–4 weeks",
    trip5: "30+ days",
    seeResult: "See my plan",
    recommends: "BlueRoam recommends",
    estimateNote: "Rough estimate (±30%). Most travelers stay safely within this.",
    daysShort: "days",
    findPlan: "Find this plan",
    startOver: "Start over",
    faqTitle: "Frequently asked",
    faq1q: "How accurate is this estimate?",
    faq1a: "Within ±30% for most travelers. Heavy video streaming on hotel Wi-Fi can lower your usage; cloud-syncing apps in the background can raise it.",
    faq2q: "What if I run out of data mid-trip?",
    faq2a: "Top up in seconds from your dashboard, or activate a fresh plan on the same eSIM. No new SIM card, no re-installing.",
    faq3q: "Do unused GBs roll over?",
    faq3a: "Within a single plan period — yes. After expiry, unused data is forfeit (industry standard). Pay & Fly wallet credit lasts 12 months.",
    faq4q: "Should I get country or regional?",
    faq4a: "One country = country plan. Hopping 2+ countries in one region = regional bundle is usually cheaper. 4+ countries across continents = Global.",
  },
  news: {
    eyebrow: "Travel pulse",
    title: "Today's intel for travelers",
    sub: "Visa changes, festival tips, connectivity flags — refreshed throughout the day.",
    updated: "Updated",
  },
  testimonials: {
    eyebrow: "Loved by travelers",
    title: "From the road, with thanks.",
    ratingNote: "4.8 average across 12,000+ travelers",
  },
  payfly: {
    badge: "PAY & FLY",
    title: "One eSIM. Every country. Pay only what you use.",
    sub: "Add credit to your BlueRoam wallet, install one eSIM, and travel — your balance is debited at local per-GB rates as you go. Valid 12 months.",
    howEyebrow: "How it works",
    howTitle: "Wallet credit, anywhere you land.",
    bullet1: "Top up €10–€500 to your wallet — 12-month validity.",
    bullet2: "Install one BlueRoam Pay & Fly eSIM (no SIM swap on each border).",
    bullet3: "Land in any of 120+ countries — your balance is consumed at local rates.",
    bullet4: "Optional auto-top-up keeps you online — opt-in, never surprise charges.",
    statValidity: "months",
    statFromGB: "per GB from",
    topupTitle: "Fund your wallet",
    topupSub: "Pick a preset or set any amount from €5 to €500.",
    customAmount: "Custom amount (€5–€500)",
    fund: "Fund wallet with",
    optInNote: "Auto-top-up is opt-in. We email a reminder before charging.",
    ratesEyebrow: "Country rates",
    ratesTitle: "What you pay per GB while traveling",
    perGb: "per GB",
    walletTitle: "Pay & Fly wallet",
    walletEmpty: "Add credit to start traveling.",
    balance: "Balance",
    autoTopup: "Auto top-up at €5",
    addCredit: "Add credit",
  },
  nav: {
    home: "Home",
    plans: "Destinations",
    howItWorks: "How it works",
    support: "Help",
    login: "Log in",
    signup: "Sign up",
  },
  home: {
    heroBadge: "Trusted in 120+ countries",
    heroTitle: "Travel light.\nStay online.",
    heroSub: "One QR code. Zero roaming charges. You're online before wheels-up.",
    heroLivePill: "travelers connecting right now",
    heroLocalEn: "See UK plans",
    heroLocalDe: "Germany plans — see prices",
    heroLocalFr: "See France plans",
    heroLocalEs: "See Spain plans",
    heroLocalIt: "See Italy plans",
    heroLocalTr: "See Türkiye plans",
    heroLocalEl: "See Greece plans",
    heroLocalAr: "See Morocco plans",
    heroLocalJa: "See Japan plans",
    heroLocalZh: "See China plans",
    cta: "Find my plan",
    ctaSecondary: "How it works",
    statPlans: "data plans",
    statCountries: "countries",
    statActivation: "to connect",
    statSupport: "human support",
    popularEyebrow: "Traveler favorites",
    popularTitle: "Where are your next steps?",
    popularSub: "From week‑long escapes to a 35‑country road trip, pick the plan that matches your journey.",
    seeAll: "See all destinations",
    howEyebrow: "How it works",
    howTitle: "Three taps. You're connected.",
    step1Title: "Pick your trip",
    step1Desc: "Choose a country or region and the data bundle that fits your stay.",
    step2Title: "Pay, quick and safe",
    step2Desc: "Card payment powered by Stripe. No account needed to buy — guest checkout in seconds.",
    step3Title: "Scan. You're on.",
    step3Desc: "Your QR code and one‑tap install link arrive by email. Add the eSIM and land connected.",
    trustEyebrow: "Why travelers pick BlueRoam",
    trustTitle: "The simplest way to stay connected abroad.",
    trustBullet1: "Instant delivery — your eSIM lands in your inbox before your flight even boards.",
    trustBullet2: "Transparent prices in EUR. No surprise roaming charges when you get home.",
    trustBullet3: "Works on every modern phone: iPhone XS+, Pixel 3+, Galaxy S20+ and more.",
    faqEyebrow: "Quick answers",
    faqTitle: "Everything you wanted to ask.",
    faq1q: "What is an eSIM?",
    faq1a: "An eSIM is a digital SIM card built into your phone. You activate it with a QR code — no plastic SIM, no swapping, no shipping.",
    faq2q: "Will it work on my phone?",
    faq2a: "BlueRoam works on iPhone XS and newer, Pixel 3 and newer, Galaxy S20 and newer, plus most flagship Androids. If you're unsure, just check Settings → Add eSIM.",
    faq3q: "When does my plan start?",
    faq3a: "Most plans activate the moment your phone connects to a local network. You can install the eSIM days before you fly without burning any data.",
    faq4q: "Can I keep my normal number?",
    faq4a: "Yes. Your BlueRoam eSIM runs alongside your usual SIM, so you keep receiving calls and messages while using local data.",
    ctaBandTitle: "Your trip. Online. In 90 seconds.",
    ctaBandSub: "500+ data plans in 120+ countries. Instant delivery by email.",

    explainEyebrow: "How BlueRoam works",
    explainTitle: "Two ways to stay connected abroad.",
    explainSub: "Pick a data plan for a single trip — or open a Pay & Fly wallet that follows you across every country, every time.",
    explainOneTitle: "Country plans",
    explainOneDesc: "Know exactly where you're going? Grab a fixed data bundle for that country or region. One price, one QR code, no surprises.",
    explainOneBullet1: "120+ countries & regional bundles (Europe, Asia, Global)",
    explainOneBullet2: "From 1 GB / 7 days up to unlimited 30-day plans",
    explainOneBullet3: "Delivered by email in seconds after checkout",
    explainOneCta: "Browse country plans",
    explainTwoTitle: "Pay & Fly wallet",
    explainTwoDesc: "Perfect for frequent flyers and multi-country trips. Top up once, install a single eSIM, and your balance is debited at local rates as you roam.",
    explainTwoBullet1: "One eSIM for all 120+ countries — no reinstall between borders",
    explainTwoBullet2: "Pay only for data you actually use, at transparent per-GB rates",
    explainTwoBullet3: "12-month validity · optional auto top-up · opt-in anytime",
    explainTwoCta: "Open my wallet",

    payflyBandEyebrow: "Pay & Fly — for travelers on the move",
    payflyBandTitle: "One wallet. Every border. Pay per GB you actually use.",
    payflyBandDesc: "Stop buying a new eSIM every country. Fund your wallet with €10–€500, install a single BlueRoam Pay & Fly eSIM, and your balance flexes with your itinerary.",
    payflyStep1Title: "Top up your wallet",
    payflyStep1Desc: "Add €10 to €500. Credit is valid for 12 months.",
    payflyStep2Title: "Install one eSIM",
    payflyStep2Desc: "A single QR code, installed once. No swapping at every border.",
    payflyStep3Title: "Pay only for what you use",
    payflyStep3Desc: "Your balance is deducted at transparent local per-GB rates.",

    walletCtaEyebrow: "Already have a Pay & Fly wallet?",
    walletCtaTitle: "Log in to top up or check your balance.",
    walletCtaDesc: "See your current balance, set an auto top-up threshold, or add more credit in a couple of taps.",
    walletCtaLogin: "Log in & top up",
    walletCtaCreate: "Create a wallet",
    walletCtaGuest: "New here? Open a free Pay & Fly wallet in under a minute.",
  },
  plans: {
    title: "Find your plan",
    subtitle: "Fair prices. Local speeds. Delivered in seconds.",
    all: "All destinations",
    empty: "No plans match that search. Try another country.",
    regions: "Regions",
  },
  checkout: {
    title: "You're almost connected",
    planSummary: "Plan summary",
    totalDue: "Total",
    emailLabel: "Email for your QR code",
    emailHelp: "We'll send your eSIM and setup link to this address.",
    terms: "Secure payment by Stripe · Cards processed in EUR.",
    payNow: "Pay & get my eSIM",
    processing: "Taking you to Stripe…",
    successTitle: "Payment received",
    successSub: "We're issuing your eSIM now. It usually takes 5 to 20 seconds.",
    waiting: "Generating your eSIM…",
    esimReady: "Your eSIM is ready to scan",
    scanQR: "On your phone, open Settings → Cellular → Add eSIM and scan this code.",
    iosInstall: "Install on iPhone (one tap)",
    passport: "Open eSIM Passport",
    trouble: "Having trouble? Your eSIM is safely stored in your dashboard — you can install it any time.",
    iccid: "ICCID",
    failedTitle: "We're still working on it",
    failedSub: "Payment went through, but provisioning is retrying. Refresh in a moment or contact support — we'll make it right.",
    copyLPA: "Copy activation code",
    copied: "Copied",
  },
  dash: {
    title: "My eSIMs",
    subtitle: "All your data plans, usage and orders in one place.",
    activeEsims: "Active eSIMs",
    recentOrders: "Recent orders",
    noEsims: "You don't have any eSIMs yet — grab one for your next trip.",
    browse: "Find a plan",
    dataUsed: "used",
    dataLeft: "left",
    expires: "Expires",
    viewDetails: "View details",
    orderId: "Order",
    amount: "Amount",
  },
  esim: {
    details: "eSIM details",
    installation: "Install",
    usage: "Usage",
    qrLabel: "Activation QR code",
  },
  auth: {
    loginTitle: "Welcome back",
    loginSub: "Log in to manage your trips and eSIMs.",
    noAccount: "New to BlueRoam?",
    createOne: "Create an account",
    registerTitle: "Create your BlueRoam account",
    registerSub: "Keep your eSIMs in one place and travel lighter next time.",
    haveAccount: "Already have an account?",
    loginHere: "Log in",
    invalid: "That email and password don't match.",
  },
  admin: {
    title: "Operations",
    subtitle: "Back office",
    stats: "Overview",
    orders: "Orders",
    customers: "Customers",
    esims: "eSIMs",
    catalog: "Catalog",
    syncPlans: "Sync catalog",
    synced: "Catalog synced",
    markup: "Markup",
    manualPrice: "Manual price",
    wholesale: "Wholesale",
    retail: "Retail",
    featured: "Featured",
    enabled: "Enabled",
    totalOrders: "Orders",
    fulfilled: "Fulfilled",
    failed: "Failed",
    revenue: "Revenue (EUR)",
    customersStat: "Customers",
    activePlans: "Active plans",
    esimsIssued: "eSIMs issued",
    retryProv: "Retry",
    retrySent: "Retry queued",
    customer: "Customer",
    orderCount: "Orders",
    esimCount: "eSIMs",
  },
  footer: {
    tagline: "Your pocket connection to the world.",
    product: "Product",
    company: "Company",
    legal: "Legal",
    terms: "Terms",
    refunds: "Refund policy",
    amlkyc: "AML / KYC policy",
    cookiePrefs: "Cookie preferences",
    cookieBanner: {
      title: "We use cookies to keep BlueRoam running smoothly.",
      body: "Strictly necessary cookies are always on. Analytics and marketing cookies are off until you allow them. You can change your mind any time from the footer.",
      learnMore: "Cookie Notice",
      preferences: "Preferences",
      reject: "Reject non-essential",
      accept: "Accept all",
      save: "Save preferences",
      prefsTitle: "Cookie preferences",
      prefsIntro: "Choose which categories of cookies BlueRoam can set on your device. You can change this any time from the website footer.",
      gpcTitle: "Global Privacy Control detected.",
      gpcBody: "We honour your GPC signal and have turned off analytics and marketing cookies. You can override that here if you want — it only affects this browser.",
      catNec: "Strictly necessary",
      catNecDesc: "Required for login, checkout, language preference and security. Cannot be disabled.",
      catAna: "Analytics",
      catAnaDesc: "Aggregate usage data so we can improve the site. IP addresses are truncated.",
      catMkt: "Marketing",
      catMktDesc: "Personalised messaging and cross-context advertising. Off by default. We do not sell personal information.",
    },
    doNotSell: "Do Not Sell or Share My Personal Information",
    limitSensitive: "Limit the Use of My Sensitive Personal Information",
    websiteTerms: "Website terms",
    support: "Support",
    privacy: "Privacy",
    usPrivacy: "US privacy notice",
    cookies: "Cookies",
    about: "About",
    contact: "Contact",
    status: "Status",
    copyright: "© 2026 BlueRoam. All rights reserved.",
  },
};

const tr = {
  common: {
    brand: "BlueRoam", tagline: "Hafif seyahat et. Hep bağlan.",
    searchPlaceholder: "Nereye gidiyorsun?",
    buy: "Bu paketi al", continue: "Devam et", back: "Geri",
    email: "E-posta", password: "Şifre", name: "Ad",
    login: "Giriş", register: "Hesap oluştur", logout: "Çıkış",
    dashboard: "eSIM'lerim", admin: "Yönetim", loading: "Yükleniyor…",
    error: "Bir şeyler ters gitti", price: "Fiyat", from: "başlangıç",
    data: "Veri", days: "Gün", country: "Ülke", plans: "paket",
    cancel: "İptal", save: "Kaydet", retry: "Tekrar dene",
    status: "Durum", actions: "İşlemler", language: "Dil", close: "Kapat",
    welcome: "Hoş geldin", destination: "Varış", yourEmail: "E-postan",
    payWith: "Kart ile öde", unlimited: "Sınırsız", moreCountries: "ülke daha", bubbleReady: "eSIM HAZIR", bubbleConnected: "Paris'te bağlı", bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 GB · 30 gün", topup: "Yenile / Yükle", topupTitle: "Bu eSIM'i yenile", topupSub: "Yeni paket seç — verin aynı eSIM'de birikir", sales: "Satış", export: "CSV indir", chartRevenue: "Gelir", chartOrders: "Sipariş", last30Days: "Son 30 gün", topCountries: "En iyi ülkeler", topPlans: "En iyi paketler", aov: "Ort. sipariş", conversion: "Dönüşüm", fulfillment: "Tamamlanma oranı", newCustomers: "Yeni müşteriler", topupCount: "Yenilemeler",
  },
  nav: { home: "Ana sayfa", plans: "Destinasyonlar", howItWorks: "Nasıl çalışır", support: "Yardım", login: "Giriş", signup: "Kayıt ol" },
  home: {
    heroBadge: "120+ ülkede güvenilir",
    heroTitle: "Hafif seyahat et.\nHep bağlan.",
    heroSub: "Tek QR kod. Sıfır roaming. Kalkıştan önce online.",
    cta: "Paketimi bul", ctaSecondary: "Nasıl çalışır",
    statPlans: "veri paketi", statCountries: "ülke",
    statActivation: "bağlantı süresi", statSupport: "gerçek destek",
    popularEyebrow: "Gezginlerin favorileri",
    popularTitle: "Bir sonraki durağın neresi?",
    popularSub: "Bir haftalık kaçamaklardan 35 ülkelik yolculuklara kadar, sana uyan paketi seç.",
    seeAll: "Tüm destinasyonlar",
    howEyebrow: "Nasıl çalışır",
    howTitle: "Üç dokunuş. Bağlandın.",
    step1Title: "Yolculuğunu seç",
    step1Desc: "Bir ülke ya da bölge seç, kalışına uygun veri paketini al.",
    step2Title: "Hızlı ve güvenli öde",
    step2Desc: "Stripe güvenceli kart ödemesi. Satın almak için hesap gerekmez — misafir ödeme.",
    step3Title: "Tara. Hazırsın.",
    step3Desc: "QR kodun ve tek dokunuşluk kurulum linkin e-postana gelir. eSIM'i ekle, indiğinde bağlı ol.",
    trustEyebrow: "Gezginler neden BlueRoam'yi seçiyor",
    trustTitle: "Yurt dışında bağlı kalmanın en kolay yolu.",
    trustBullet1: "Anında teslimat — eSIM'in uçuşun başlamadan e-postana düşer.",
    trustBullet2: "EUR cinsinden şeffaf fiyatlar. Eve döndüğünde sürpriz roaming faturası yok.",
    trustBullet3: "Tüm modern telefonlarla uyumlu: iPhone XS+, Pixel 3+, Galaxy S20+ ve daha fazlası.",
    faqEyebrow: "Hızlı cevaplar",
    faqTitle: "Sormak istediğin her şey.",
    faq1q: "eSIM nedir?",
    faq1a: "eSIM, telefonunun içinde gömülü dijital bir SIM karttır. QR kodla aktive edilir — plastik SIM, değişim, kargo derdi yok.",
    faq2q: "Telefonumda çalışır mı?",
    faq2a: "BlueRoam; iPhone XS ve üstü, Pixel 3 ve üstü, Galaxy S20 ve üstü ile çoğu amiral Android'de çalışır. Emin değilsen Ayarlar → eSIM Ekle menüsünü kontrol et.",
    faq3q: "Paketim ne zaman başlar?",
    faq3a: "Çoğu paket, telefonun yerel ağa bağlandığı anda aktive olur. eSIM'i uçmadan günler önce kurup hiç veri harcamayabilirsin.",
    faq4q: "Normal numaramı koruyabilir miyim?",
    faq4a: "Evet. BlueRoam eSIM'i normal SIM'inle paralel çalışır, arama ve mesajları almaya devam edersin.",
    ctaBandTitle: "Yolculuğun. Online. 90 saniyede.",
    ctaBandSub: "120+ ülkede 500+ veri paketi. E-posta ile anında teslim.",
    heroLivePill: "yolcu şu an bağlanıyor",
    heroLocalTr: "Türkiye paketleri — fiyatları gör",
    explainEyebrow: "BlueRoam nasıl çalışır",
    explainTitle: "Yurt dışında bağlı kalmanın iki yolu.",
    explainSub: "Tek seferlik yolculuk için veri paketi seçin — ya da her sınırda yanınızda gezen bir Pay & Fly cüzdanı açın.",
    explainOneTitle: "Ülke paketleri",
    explainOneDesc: "Nereye gittiğinizi biliyor musunuz? O ülke veya bölge için sabit bir veri paketi alın. Tek fiyat, tek QR, sürpriz yok.",
    explainOneBullet1: "120+ ülke ve bölgesel paketler (Avrupa, Asya, Global)",
    explainOneBullet2: "1 GB / 7 gün'den sınırsız 30 günlük paketlere",
    explainOneBullet3: "Ödeme sonrası saniyeler içinde e-posta ile teslim",
    explainOneCta: "Ülke paketlerine göz at",
    explainTwoTitle: "Pay & Fly cüzdanı",
    explainTwoDesc: "Sık uçanlar ve çok ülkeli geziler için ideal. Bir kez yükleyin, tek eSIM takın; bakiye yerel tarifelerle düşer.",
    explainTwoBullet1: "Tüm 120+ ülke için tek eSIM — sınırda yeniden kurulum yok",
    explainTwoBullet2: "Sadece kullandığınız veri için şeffaf GB başı fiyatlar",
    explainTwoBullet3: "12 ay geçerlilik · isteğe bağlı otomatik yükleme",
    explainTwoCta: "Nasıl çalıştığını gör",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "Tek cüzdan. Her sınır. GB başı öde.",
    payflyBandDescShort: "Yükle, tek eSIM kur, gezerken yerel tarifelerle düşelim. Demoyu dene →",
    payflyStep1TitleShort: "Yükle",
    payflyStep2TitleShort: "eSIM kur",
    payflyStep3TitleShort: "Uç & kullan",
    walletCtaGo: "Cüzdanımı aç",
    walletCtaCreateShort: "Cüzdanımı başlat",
    walletCtaLoginShort: "Giriş",
    finalEyebrow: "Hazır mısın?",
  },
  plans: { title: "Paketini bul", subtitle: "Adil fiyatlar. Yerel hızlar. Saniyeler içinde teslim.", all: "Tüm destinasyonlar", empty: "Aramana uygun paket yok. Başka bir ülke dene.", regions: "Bölgeler" },
  checkout: {
    title: "Neredeyse bağlandın",
    planSummary: "Paket özeti", totalDue: "Toplam",
    emailLabel: "QR kodun için e-posta", emailHelp: "eSIM ve kurulum linkini bu adrese göndereceğiz.",
    terms: "Stripe ile güvenli ödeme · Kartlar EUR olarak işlenir.",
    payNow: "Öde ve eSIM'imi al", processing: "Stripe'a yönlendiriliyor…",
    successTitle: "Ödeme alındı", successSub: "eSIM'ini şu an hazırlıyoruz. Genellikle 5–20 saniye sürer.",
    waiting: "eSIM'in oluşturuluyor…",
    esimReady: "eSIM'in tarayıp kurmaya hazır",
    scanQR: "Telefonunda Ayarlar → Hücresel → eSIM Ekle'ye git ve bu kodu tara.",
    iosInstall: "iPhone'a kur (tek dokunuş)", passport: "eSIM Pasaportu aç",
    trouble: "Sorun mu var? eSIM'in panelinde güvenle duruyor — istediğin zaman kurabilirsin.",
    iccid: "ICCID",
    failedTitle: "Hâlâ üzerinde çalışıyoruz",
    failedSub: "Ödeme tamam, hazırlama tekrar deneniyor. Birazdan yenile ya da destek ekibimizle iletişime geç — düzelteceğiz.",
    copyLPA: "Aktivasyon kodunu kopyala", copied: "Kopyalandı",
  },
  dash: {
    title: "eSIM'lerim", subtitle: "Tüm veri paketlerin, kullanımın ve siparişlerin tek yerde.",
    activeEsims: "Aktif eSIM'ler", recentOrders: "Son siparişler",
    noEsims: "Henüz eSIM'in yok — bir sonraki yolculuğun için bir tane kap.",
    browse: "Paket bul", dataUsed: "kullanıldı", dataLeft: "kaldı",
    expires: "Bitiş", viewDetails: "Detaylar", orderId: "Sipariş", amount: "Tutar",
  },
  esim: { details: "eSIM detayları", installation: "Kurulum", usage: "Kullanım", qrLabel: "Aktivasyon QR kodu" },
  auth: {
    loginTitle: "Tekrar hoş geldin", loginSub: "Yolculuklarını ve eSIM'lerini yönetmek için giriş yap.",
    noAccount: "BlueRoam'de yeni misin?", createOne: "Hesap oluştur",
    registerTitle: "BlueRoam hesabını oluştur", registerSub: "eSIM'lerini tek yerde topla ve bir sonraki sefer daha hafif seyahat et.",
    haveAccount: "Hesabın var mı?", loginHere: "Giriş yap",
    invalid: "E-posta ve şifre eşleşmiyor.",
  },
  admin: {
    title: "Operasyon", subtitle: "Yönetim paneli", stats: "Genel bakış",
    orders: "Siparişler", customers: "Müşteriler", esims: "eSIM'ler", catalog: "Katalog",
    syncPlans: "Kataloğu senkronize et", synced: "Katalog senkronize edildi",
    markup: "Kâr oranı", manualPrice: "Manuel fiyat", wholesale: "Toptan", retail: "Perakende",
    featured: "Öne çıkan", enabled: "Aktif",
    totalOrders: "Siparişler", fulfilled: "Tamamlanan", failed: "Başarısız", revenue: "Gelir (EUR)",
    customersStat: "Müşteriler", activePlans: "Aktif paketler", esimsIssued: "Verilen eSIM'ler",
    retryProv: "Tekrar dene", retrySent: "Tekrar denemede",
    customer: "Müşteri", orderCount: "Siparişler", esimCount: "eSIM'ler",
  },
  footer: {
    tagline: "Dünyayla cep bağlantın.",
    product: "Ürün", company: "Şirket", legal: "Yasal",
    terms: "Şartlar", privacy: "Gizlilik", cookies: "Çerezler",
    usPrivacy: "ABD gizlilik bildirimi",
    refunds: "İade politikası",
    amlkyc: "AML / KYC politikası",
    cookiePrefs: "Çerez tercihleri",
    cookieBanner: {
      title: "BlueRoam'un sorunsuz çalışması için çerez kullanıyoruz.",
      body: "Zorunlu çerezler her zaman açıktır. Analitik ve pazarlama çerezleri siz izin verene kadar kapalıdır. Fikrinizi her zaman alttaki menüden değiştirebilirsiniz.",
      learnMore: "Çerez Bildirimi",
      preferences: "Tercihler",
      reject: "Zorunlu olmayanları reddet",
      accept: "Tümünü kabul et",
      save: "Tercihleri kaydet",
      prefsTitle: "Çerez tercihleri",
      prefsIntro: "BlueRoam'un cihazınızda hangi çerezleri kullanabileceğini seçin. Bunu her zaman alttaki menüden değiştirebilirsiniz.",
      gpcTitle: "Global Privacy Control algılandı.",
      gpcBody: "GPC sinyalinize uyduk; analitik ve pazarlama çerezlerini kapattık. Bu tarayıcı için değiştirebilirsiniz.",
      catNec: "Zorunlu",
      catNecDesc: "Giriş, ödeme, dil tercihi ve güvenlik için gerekli. Devre dışı bırakılamaz.",
      catAna: "Analitik",
      catAnaDesc: "Siteyi geliştirmek için toplu kullanım verileri. IP adresleri kısaltılır.",
      catMkt: "Pazarlama",
      catMktDesc: "Kişiselleştirilmiş mesajlar ve bağlam dışı reklamcılık. Varsayılan kapalı. Kişisel bilgi satmıyoruz.",
    },
    doNotSell: "Kişisel Bilgilerimi Satmayın veya Paylaşmayın",
    limitSensitive: "Hassas Kişisel Bilgilerin Kullanımını Sınırla",
    websiteTerms: "Web sitesi şartları",
    support: "Destek",
    about: "Hakkımızda", contact: "İletişim", status: "Durum",
    copyright: "© 2026 BlueRoam. Tüm hakları saklıdır.",
  },
};

const fr = {
  common: {
    brand: "BlueRoam", tagline: "Voyagez léger. Restez connecté.",
    searchPlaceholder: "Où partez-vous ?",
    buy: "Choisir ce forfait", continue: "Continuer", back: "Retour",
    email: "E-mail", password: "Mot de passe", name: "Nom",
    login: "Connexion", register: "Créer un compte", logout: "Déconnexion",
    dashboard: "Mes eSIMs", admin: "Admin", loading: "Chargement…",
    error: "Une erreur est survenue", price: "Prix", from: "à partir de",
    data: "Données", days: "Jours", country: "Pays", plans: "forfaits",
    cancel: "Annuler", save: "Enregistrer", retry: "Réessayer",
    status: "Statut", actions: "Actions", language: "Langue", close: "Fermer",
    welcome: "Bienvenue", destination: "Destination", yourEmail: "Votre e-mail",
    payWith: "Payer par carte", unlimited: "Illimité", moreCountries: "autres pays", bubbleReady: "eSIM PRÊTE", bubbleConnected: "Connecté à Paris", bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 Go · 30 jours", topup: "Renouveler / Recharger", topupTitle: "Renouveler cette eSIM", topupSub: "Choisissez un nouveau forfait — vos données s'ajoutent sur la même eSIM", sales: "Ventes", export: "Exporter CSV", chartRevenue: "Revenus", chartOrders: "Commandes", last30Days: "30 derniers jours", topCountries: "Top pays", topPlans: "Top forfaits", aov: "Panier moyen", conversion: "Conversion", fulfillment: "Taux de réussite", newCustomers: "Nouveaux clients", topupCount: "Recharges",
  },
  nav: { home: "Accueil", plans: "Destinations", howItWorks: "Comment ça marche", support: "Aide", login: "Connexion", signup: "S'inscrire" },
  home: {
    heroBadge: "Présent dans plus de 120 pays",
    heroTitle: "Voyagez léger.\nRestez connecté.",
    heroSub: "Un QR code. Zéro frais d'itinérance. Connecté avant le décollage.",
    cta: "Trouver mon forfait", ctaSecondary: "Comment ça marche",
    statPlans: "forfaits data", statCountries: "pays",
    statActivation: "pour se connecter", statSupport: "support humain",
    popularEyebrow: "Les préférés des voyageurs",
    popularTitle: "Où vous mènent vos prochains pas ?",
    popularSub: "Des escapades d'une semaine à un road trip à travers 35 pays, choisissez le forfait qui suit votre itinéraire.",
    seeAll: "Voir toutes les destinations",
    howEyebrow: "Comment ça marche",
    howTitle: "Trois taps. Vous êtes en ligne.",
    step1Title: "Choisissez votre voyage",
    step1Desc: "Sélectionnez un pays ou une région et le volume de données adapté à votre séjour.",
    step2Title: "Paiement rapide et sûr",
    step2Desc: "Paiement par carte sécurisé par Stripe. Pas besoin de compte pour acheter — paiement invité en quelques secondes.",
    step3Title: "Scannez. Vous êtes connecté.",
    step3Desc: "Votre QR code et votre lien d'installation en un clic arrivent par e-mail. Ajoutez l'eSIM et atterrissez déjà en ligne.",
    trustEyebrow: "Pourquoi les voyageurs choisissent BlueRoam",
    trustTitle: "La façon la plus simple de rester connecté à l'étranger.",
    trustBullet1: "Livraison instantanée — votre eSIM arrive avant même votre embarquement.",
    trustBullet2: "Prix transparents en euros. Aucune mauvaise surprise d'itinérance au retour.",
    trustBullet3: "Compatible avec tous les téléphones récents : iPhone XS+, Pixel 3+, Galaxy S20+ et plus.",
    faqEyebrow: "Réponses rapides",
    faqTitle: "Tout ce que vous vouliez demander.",
    faq1q: "Qu'est-ce qu'une eSIM ?",
    faq1a: "Une eSIM est une carte SIM numérique intégrée à votre téléphone. Vous l'activez avec un QR code — pas de carte physique, pas de changement, pas d'envoi.",
    faq2q: "Est-ce compatible avec mon téléphone ?",
    faq2a: "BlueRoam fonctionne sur iPhone XS et plus récent, Pixel 3 et plus récent, Galaxy S20 et plus récent, ainsi que la plupart des Android haut de gamme. Dans le doute, vérifiez Réglages → Ajouter une eSIM.",
    faq3q: "Quand mon forfait commence-t-il ?",
    faq3a: "La plupart des forfaits s'activent dès que votre téléphone se connecte à un réseau local. Vous pouvez installer l'eSIM plusieurs jours avant de partir sans consommer de données.",
    faq4q: "Puis-je garder mon numéro habituel ?",
    faq4a: "Oui. Votre eSIM BlueRoam fonctionne en parallèle de votre SIM principale, vous continuez à recevoir appels et SMS.",
    ctaBandTitle: "Votre voyage. En ligne. En 90 secondes.",
    ctaBandSub: "500+ forfaits dans plus de 120 pays. Livraison instantanée par e-mail.",
    heroLivePill: "voyageurs en cours de connexion",
    heroLocalFr: "Forfaits France — voir les prix",
    explainEyebrow: "Comment fonctionne BlueRoam",
    explainTitle: "Deux façons de rester connecté à l'étranger.",
    explainSub: "Choisissez un forfait pour un voyage — ou ouvrez un portefeuille Pay & Fly qui vous suit dans chaque pays.",
    explainOneTitle: "Forfaits par pays",
    explainOneDesc: "Vous savez où vous allez ? Prenez un forfait fixe pour ce pays ou cette région. Un prix, un QR, zéro surprise.",
    explainOneBullet1: "120+ pays et forfaits régionaux (Europe, Asie, Mondial)",
    explainOneBullet2: "De 1 Go / 7 jours aux forfaits illimités 30 jours",
    explainOneBullet3: "Livré par e-mail en quelques secondes",
    explainOneCta: "Voir les forfaits pays",
    explainTwoTitle: "Portefeuille Pay & Fly",
    explainTwoDesc: "Parfait pour les grands voyageurs. Rechargez une fois, installez une seule eSIM, et votre solde est débité aux tarifs locaux.",
    explainTwoBullet1: "Une eSIM pour les 120+ pays — aucune réinstallation",
    explainTwoBullet2: "Payez uniquement les Go utilisés, à prix transparent",
    explainTwoBullet3: "12 mois de validité · recharge auto en option",
    explainTwoCta: "Voir en action",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "Un portefeuille. Toutes les frontières. Payez au Go.",
    payflyBandDescShort: "Rechargez, installez une eSIM, on débite aux tarifs locaux. Essayez la démo →",
    payflyStep1TitleShort: "Recharger",
    payflyStep2TitleShort: "Installer eSIM",
    payflyStep3TitleShort: "Voyager & utiliser",
    walletCtaGo: "Ouvrir mon portefeuille",
    walletCtaCreateShort: "Créer mon portefeuille",
    walletCtaLoginShort: "Connexion",
    finalEyebrow: "Prêt·e ?",
  },
  plans: { title: "Trouvez votre forfait", subtitle: "Des prix justes. Des vitesses locales. Livré en quelques secondes.", all: "Toutes les destinations", empty: "Aucun forfait ne correspond. Essayez un autre pays.", regions: "Régions" },
  checkout: {
    title: "Vous êtes presque en ligne",
    planSummary: "Récapitulatif du forfait", totalDue: "Total",
    emailLabel: "E-mail pour votre QR code", emailHelp: "Nous enverrons votre eSIM et le lien d'installation à cette adresse.",
    terms: "Paiement sécurisé par Stripe · Cartes traitées en EUR.",
    payNow: "Payer et recevoir mon eSIM", processing: "Redirection vers Stripe…",
    successTitle: "Paiement reçu", successSub: "Nous créons votre eSIM. Cela prend généralement 5 à 20 secondes.",
    waiting: "Génération de votre eSIM…",
    esimReady: "Votre eSIM est prête",
    scanQR: "Sur votre téléphone, ouvrez Réglages → Données cellulaires → Ajouter une eSIM et scannez ce code.",
    iosInstall: "Installer sur iPhone (en un clic)", passport: "Ouvrir le Passeport eSIM",
    trouble: "Un souci ? Votre eSIM est en sécurité dans votre tableau de bord — vous pouvez l'installer à tout moment.",
    iccid: "ICCID",
    failedTitle: "On y travaille encore",
    failedSub: "Le paiement est bien passé, mais la création de l'eSIM réessaie. Rafraîchissez dans un instant ou contactez le support — on règle ça.",
    copyLPA: "Copier le code d'activation", copied: "Copié",
  },
  dash: {
    title: "Mes eSIMs", subtitle: "Tous vos forfaits, votre consommation et vos commandes au même endroit.",
    activeEsims: "eSIMs actives", recentOrders: "Commandes récentes",
    noEsims: "Vous n'avez pas encore d'eSIM — prenez-en une pour votre prochain voyage.",
    browse: "Trouver un forfait", dataUsed: "utilisé", dataLeft: "restant",
    expires: "Expire le", viewDetails: "Voir les détails", orderId: "Commande", amount: "Montant",
  },
  esim: { details: "Détails de l'eSIM", installation: "Installation", usage: "Consommation", qrLabel: "QR code d'activation" },
  auth: {
    loginTitle: "Bon retour", loginSub: "Connectez-vous pour gérer vos voyages et vos eSIMs.",
    noAccount: "Nouveau sur BlueRoam ?", createOne: "Créer un compte",
    registerTitle: "Créez votre compte BlueRoam", registerSub: "Retrouvez toutes vos eSIMs au même endroit et voyagez encore plus léger.",
    haveAccount: "Vous avez déjà un compte ?", loginHere: "Connexion",
    invalid: "Cet e-mail et ce mot de passe ne correspondent pas.",
  },
  admin: {
    title: "Opérations", subtitle: "Back office", stats: "Vue d'ensemble",
    orders: "Commandes", customers: "Clients", esims: "eSIMs", catalog: "Catalogue",
    syncPlans: "Synchroniser le catalogue", synced: "Catalogue synchronisé",
    markup: "Marge", manualPrice: "Prix manuel", wholesale: "Prix de gros", retail: "Prix de vente",
    featured: "En vedette", enabled: "Activé",
    totalOrders: "Commandes", fulfilled: "Exécutées", failed: "Échouées", revenue: "Revenus (EUR)",
    customersStat: "Clients", activePlans: "Forfaits actifs", esimsIssued: "eSIMs émises",
    retryProv: "Réessayer", retrySent: "Réessai lancé",
    customer: "Client", orderCount: "Commandes", esimCount: "eSIMs",
  },
  footer: {
    tagline: "Votre connexion de poche au monde.",
    product: "Produit", company: "Entreprise", legal: "Légal",
    terms: "CGU", privacy: "Confidentialité", cookies: "Cookies",
    usPrivacy: "Avis de confidentialité US",
    refunds: "Politique de remboursement",
    amlkyc: "Politique AML / KYC",
    cookiePrefs: "Préférences cookies",
    cookieBanner: {
      title: "Nous utilisons des cookies pour faire fonctionner BlueRoam.",
      body: "Les cookies strictement nécessaires sont toujours activés. Les cookies analytiques et marketing sont désactivés tant que vous ne les acceptez pas. Modifiable à tout moment depuis le pied de page.",
      learnMore: "Avis sur les cookies",
      preferences: "Préférences",
      reject: "Refuser les non-essentiels",
      accept: "Tout accepter",
      save: "Enregistrer",
      prefsTitle: "Préférences cookies",
      prefsIntro: "Choisissez les catégories de cookies que BlueRoam peut déposer sur votre appareil. Modifiable à tout moment.",
      gpcTitle: "Global Privacy Control détecté.",
      gpcBody: "Nous respectons votre signal GPC et avons désactivé les cookies analytiques et marketing. Vous pouvez modifier cela ici si vous le souhaitez — uniquement pour ce navigateur.",
      catNec: "Strictement nécessaires",
      catNecDesc: "Connexion, paiement, préférence linguistique et sécurité. Non désactivables.",
      catAna: "Analytique",
      catAnaDesc: "Données d'usage agrégées pour améliorer le site. Les adresses IP sont tronquées.",
      catMkt: "Marketing",
      catMktDesc: "Communications personnalisées et publicité inter-contextuelle. Désactivés par défaut. Nous ne vendons pas vos données.",
    },
    doNotSell: "Ne pas vendre ni partager mes informations personnelles",
    limitSensitive: "Limiter l’utilisation de mes informations personnelles sensibles",
    websiteTerms: "Conditions du site",
    support: "Assistance",
    about: "À propos", contact: "Contact", status: "Statut",
    copyright: "© 2026 BlueRoam. Tous droits réservés.",
  },
};

const de = {
  common: {
    brand: "BlueRoam", tagline: "Leicht reisen. Immer online.",
    searchPlaceholder: "Wohin geht es?",
    buy: "Tarif wählen", continue: "Weiter", back: "Zurück",
    email: "E-Mail", password: "Passwort", name: "Name",
    login: "Anmelden", register: "Konto erstellen", logout: "Abmelden",
    dashboard: "Meine eSIMs", admin: "Admin", loading: "Lädt…",
    error: "Etwas ist schiefgelaufen", price: "Preis", from: "ab",
    data: "Daten", days: "Tage", country: "Land", plans: "Tarife",
    cancel: "Abbrechen", save: "Speichern", retry: "Erneut versuchen",
    status: "Status", actions: "Aktionen", language: "Sprache", close: "Schließen",
    welcome: "Willkommen", destination: "Reiseziel", yourEmail: "Deine E-Mail",
    payWith: "Mit Karte zahlen", unlimited: "Unbegrenzt", moreCountries: "weitere Länder", bubbleReady: "eSIM BEREIT", bubbleConnected: "Verbunden in Paris", bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 GB · 30 Tage", topup: "Verlängern / Aufladen", topupTitle: "Diese eSIM verlängern", topupSub: "Wähl einen neuen Tarif — die Daten kommen auf dieselbe eSIM", sales: "Umsatz", export: "CSV exportieren", chartRevenue: "Umsatz", chartOrders: "Bestellungen", last30Days: "Letzte 30 Tage", topCountries: "Top-Länder", topPlans: "Top-Tarife", aov: "Ø Bestellung", conversion: "Conversion", fulfillment: "Erfüllungsrate", newCustomers: "Neue Kunden", topupCount: "Aufladungen",
  },
  nav: { home: "Start", plans: "Reiseziele", howItWorks: "So funktioniert's", support: "Hilfe", login: "Anmelden", signup: "Registrieren" },
  home: {
    heroBadge: "Vertraut in 120+ Ländern",
    heroTitle: "Leicht reisen.\nImmer online.",
    heroSub: "Ein QR-Code. Keine Roaming-Kosten. Online vor dem Start.",
    cta: "Tarif finden", ctaSecondary: "So funktioniert's",
    statPlans: "Datentarife", statCountries: "Länder",
    statActivation: "bis online", statSupport: "echter Support",
    popularEyebrow: "Beliebt bei Reisenden",
    popularTitle: "Wohin führen dich deine nächsten Schritte?",
    popularSub: "Von Wochenendtrips bis zur 35-Länder-Rundreise — wähl den Tarif, der zu deiner Route passt.",
    seeAll: "Alle Reiseziele ansehen",
    howEyebrow: "So funktioniert's",
    howTitle: "Drei Klicks. Du bist verbunden.",
    step1Title: "Reise auswählen",
    step1Desc: "Wähl ein Land oder eine Region und das Datenpaket, das zu deinem Aufenthalt passt.",
    step2Title: "Schnell und sicher bezahlen",
    step2Desc: "Kartenzahlung über Stripe. Kein Konto erforderlich — Gast-Checkout in Sekunden.",
    step3Title: "Scannen. Bist online.",
    step3Desc: "QR-Code und Ein-Klick-Installationslink kommen per E-Mail. eSIM hinzufügen, verbunden landen.",
    trustEyebrow: "Warum Reisende BlueRoam wählen",
    trustTitle: "Der einfachste Weg, unterwegs online zu bleiben.",
    trustBullet1: "Sofortlieferung — deine eSIM liegt in deinem Postfach, bevor dein Flug aufruft.",
    trustBullet2: "Transparente Preise in Euro. Keine bösen Roaming-Überraschungen zuhause.",
    trustBullet3: "Läuft auf jedem modernen Handy: iPhone XS+, Pixel 3+, Galaxy S20+ und mehr.",
    faqEyebrow: "Kurze Antworten",
    faqTitle: "Alles, was du wissen wolltest.",
    faq1q: "Was ist eine eSIM?",
    faq1a: "Eine eSIM ist eine digitale SIM-Karte, die fest in deinem Handy sitzt. Du aktivierst sie mit einem QR-Code — keine Plastik-SIM, kein Wechseln, kein Versand.",
    faq2q: "Funktioniert das mit meinem Handy?",
    faq2a: "BlueRoam läuft auf iPhone XS und neuer, Pixel 3 und neuer, Galaxy S20 und neuer sowie den meisten aktuellen Android-Flaggschiffen. Unsicher? Prüf einfach Einstellungen → eSIM hinzufügen.",
    faq3q: "Wann startet mein Tarif?",
    faq3a: "Die meisten Tarife starten, sobald dein Handy sich mit einem lokalen Netz verbindet. Du kannst die eSIM Tage vor Abflug installieren, ohne Datenvolumen zu verlieren.",
    faq4q: "Kann ich meine normale Nummer behalten?",
    faq4a: "Ja. Deine BlueRoam-eSIM läuft parallel zu deiner Haupt-SIM — Anrufe und Nachrichten empfängst du ganz normal weiter.",
    ctaBandTitle: "Deine Reise. Online. In 90 Sekunden.",
    ctaBandSub: "500+ Datentarife in 120+ Ländern. Lieferung sofort per E-Mail.",
    heroLivePill: "Reisende verbinden sich gerade",
    heroLocalDe: "Deutschland-Tarife — Preise ansehen",
    explainEyebrow: "So funktioniert BlueRoam",
    explainTitle: "Zwei Wege, im Ausland online zu bleiben.",
    explainSub: "Wähle einen Datentarif für eine Reise — oder öffne ein Pay & Fly Wallet, das dich in jedem Land begleitet.",
    explainOneTitle: "Länder-Tarife",
    explainOneDesc: "Du weißt, wohin es geht? Hol dir ein festes Datenpaket für das Land oder die Region. Ein Preis, ein QR, keine Überraschungen.",
    explainOneBullet1: "120+ Länder & Regionalpakete (Europa, Asien, Global)",
    explainOneBullet2: "Von 1 GB / 7 Tagen bis unbegrenzt 30 Tage",
    explainOneBullet3: "Per E-Mail in Sekunden geliefert",
    explainOneCta: "Länder-Tarife ansehen",
    explainTwoTitle: "Pay & Fly Wallet",
    explainTwoDesc: "Perfekt für Vielflieger und Mehrländer-Trips. Einmal aufladen, eine eSIM installieren, Guthaben wird zu lokalen Raten belastet.",
    explainTwoBullet1: "Eine eSIM für alle 120+ Länder — keine Neuinstallation",
    explainTwoBullet2: "Zahle nur für verbrauchte Daten, transparent pro GB",
    explainTwoBullet3: "12 Monate gültig · optionales Auto-Top-up",
    explainTwoCta: "Live ansehen",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "Ein Wallet. Jede Grenze. Pro GB bezahlen.",
    payflyBandDescShort: "Aufladen, eine eSIM installieren, zu lokalen Raten abrechnen. Demo ausprobieren →",
    payflyStep1TitleShort: "Aufladen",
    payflyStep2TitleShort: "eSIM installieren",
    payflyStep3TitleShort: "Fliegen & nutzen",
    walletCtaGo: "Mein Wallet öffnen",
    walletCtaCreateShort: "Wallet starten",
    walletCtaLoginShort: "Einloggen",
    finalEyebrow: "Bereit?",
  },
  plans: { title: "Finde deinen Tarif", subtitle: "Faire Preise. Lokale Speeds. In Sekunden geliefert.", all: "Alle Reiseziele", empty: "Keine Tarife gefunden. Probier ein anderes Land.", regions: "Regionen" },
  checkout: {
    title: "Gleich bist du online",
    planSummary: "Tarifübersicht", totalDue: "Gesamt",
    emailLabel: "E-Mail für deinen QR-Code", emailHelp: "Wir schicken dir eSIM und Setup-Link an diese Adresse.",
    terms: "Sichere Zahlung via Stripe · Karten in EUR abgerechnet.",
    payNow: "Zahlen & eSIM erhalten", processing: "Weiterleitung zu Stripe…",
    successTitle: "Zahlung eingegangen", successSub: "Wir erstellen gerade deine eSIM. Dauert meistens 5 bis 20 Sekunden.",
    waiting: "eSIM wird erstellt…",
    esimReady: "Deine eSIM ist bereit",
    scanQR: "Öffne auf deinem Handy Einstellungen → Mobilfunk → eSIM hinzufügen und scanne diesen Code.",
    iosInstall: "Auf iPhone installieren (ein Klick)", passport: "eSIM-Pass öffnen",
    trouble: "Probleme? Deine eSIM ist sicher in deinem Dashboard gespeichert — du kannst sie jederzeit installieren.",
    iccid: "ICCID",
    failedTitle: "Wir arbeiten noch dran",
    failedSub: "Die Zahlung war erfolgreich, aber die Bereitstellung läuft noch. Lade gleich neu oder melde dich beim Support — wir regeln das.",
    copyLPA: "Aktivierungscode kopieren", copied: "Kopiert",
  },
  dash: {
    title: "Meine eSIMs", subtitle: "Alle deine Tarife, deine Nutzung und Bestellungen an einem Ort.",
    activeEsims: "Aktive eSIMs", recentOrders: "Letzte Bestellungen",
    noEsims: "Noch keine eSIM — hol dir eine für deine nächste Reise.",
    browse: "Tarif finden", dataUsed: "verbraucht", dataLeft: "verbleibend",
    expires: "Läuft ab", viewDetails: "Details ansehen", orderId: "Bestellung", amount: "Betrag",
  },
  esim: { details: "eSIM-Details", installation: "Installation", usage: "Nutzung", qrLabel: "Aktivierungs-QR-Code" },
  auth: {
    loginTitle: "Willkommen zurück", loginSub: "Melde dich an, um deine Reisen und eSIMs zu verwalten.",
    noAccount: "Neu bei BlueRoam?", createOne: "Konto erstellen",
    registerTitle: "Erstelle dein BlueRoam-Konto", registerSub: "Behalte deine eSIMs an einem Ort und reise beim nächsten Mal noch leichter.",
    haveAccount: "Bereits ein Konto?", loginHere: "Anmelden",
    invalid: "E-Mail und Passwort stimmen nicht überein.",
  },
  admin: {
    title: "Operations", subtitle: "Back Office", stats: "Überblick",
    orders: "Bestellungen", customers: "Kunden", esims: "eSIMs", catalog: "Katalog",
    syncPlans: "Katalog synchronisieren", synced: "Katalog synchronisiert",
    markup: "Aufschlag", manualPrice: "Manueller Preis", wholesale: "Einkaufspreis", retail: "Verkaufspreis",
    featured: "Empfohlen", enabled: "Aktiv",
    totalOrders: "Bestellungen", fulfilled: "Erfüllt", failed: "Fehlgeschlagen", revenue: "Umsatz (EUR)",
    customersStat: "Kunden", activePlans: "Aktive Tarife", esimsIssued: "Ausgestellte eSIMs",
    retryProv: "Erneut versuchen", retrySent: "Wiederholung eingereiht",
    customer: "Kunde", orderCount: "Bestellungen", esimCount: "eSIMs",
  },
  footer: {
    tagline: "Deine Verbindung zur Welt — in der Hosentasche.",
    product: "Produkt", company: "Unternehmen", legal: "Rechtliches",
    terms: "AGB", privacy: "Datenschutz", cookies: "Cookies",
    usPrivacy: "US-Datenschutzhinweis",
    refunds: "Rückerstattung",
    amlkyc: "AML / KYC-Richtlinie",
    cookiePrefs: "Cookie-Einstellungen",
    cookieBanner: {
      title: "Wir verwenden Cookies, damit BlueRoam reibungslos läuft.",
      body: "Notwendige Cookies sind immer aktiv. Analyse- und Marketing-Cookies sind aus, bis Sie sie zulassen. Jederzeit über den Footer änderbar.",
      learnMore: "Cookie-Hinweis",
      preferences: "Einstellungen",
      reject: "Nicht notwendige ablehnen",
      accept: "Alle akzeptieren",
      save: "Einstellungen speichern",
      prefsTitle: "Cookie-Einstellungen",
      prefsIntro: "Wählen Sie, welche Cookie-Kategorien BlueRoam auf Ihrem Gerät setzen darf. Jederzeit im Footer änderbar.",
      gpcTitle: "Global Privacy Control erkannt.",
      gpcBody: "Wir respektieren Ihr GPC-Signal und haben Analyse- und Marketing-Cookies deaktiviert. Sie können dies hier überschreiben — gilt nur für diesen Browser.",
      catNec: "Notwendig",
      catNecDesc: "Erforderlich für Login, Checkout, Sprache und Sicherheit. Nicht deaktivierbar.",
      catAna: "Analyse",
      catAnaDesc: "Aggregierte Nutzungsdaten zur Verbesserung der Seite. IP-Adressen werden gekürzt.",
      catMkt: "Marketing",
      catMktDesc: "Personalisierte Nachrichten und kontextübergreifende Werbung. Standardmäßig aus. Wir verkaufen keine Daten.",
    },
    doNotSell: "Verkauf oder Weitergabe meiner personenbezogenen Daten verbieten",
    limitSensitive: "Nutzung meiner sensiblen personenbezogenen Daten einschränken",
    websiteTerms: "Website-Bedingungen",
    support: "Support",
    about: "Über uns", contact: "Kontakt", status: "Status",
    copyright: "© 2026 BlueRoam. Alle Rechte vorbehalten.",
  },
};

const es = {
  common: {
    brand: "BlueRoam", tagline: "Viaja ligero. Sigue en línea.",
    searchPlaceholder: "¿Dónde vas?",
    buy: "Elegir este plan", continue: "Continuar", back: "Atrás",
    email: "Correo", password: "Contraseña", name: "Nombre",
    login: "Iniciar sesión", register: "Crear cuenta", logout: "Cerrar sesión",
    dashboard: "Mis eSIMs", admin: "Admin", loading: "Cargando…",
    error: "Algo salió mal", price: "Precio", from: "desde",
    data: "Datos", days: "Días", country: "País", plans: "planes",
    cancel: "Cancelar", save: "Guardar", retry: "Reintentar",
    status: "Estado", actions: "Acciones", language: "Idioma", close: "Cerrar",
    welcome: "Bienvenido", destination: "Destino", yourEmail: "Tu correo",
    payWith: "Pagar con tarjeta", unlimited: "Ilimitado", moreCountries: "países más", bubbleReady: "eSIM LISTA", bubbleConnected: "Conectado en París", bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 GB · 30 días", topup: "Renovar / Recargar", topupTitle: "Renovar esta eSIM", topupSub: "Elige un nuevo plan — los datos se suman en la misma eSIM", sales: "Ventas", export: "Exportar CSV", chartRevenue: "Ingresos", chartOrders: "Pedidos", last30Days: "Últimos 30 días", topCountries: "Países top", topPlans: "Planes top", aov: "Pedido medio", conversion: "Conversión", fulfillment: "Tasa de cumplimiento", newCustomers: "Clientes nuevos", topupCount: "Recargas",
  },
  nav: { home: "Inicio", plans: "Destinos", howItWorks: "Cómo funciona", support: "Ayuda", login: "Iniciar sesión", signup: "Registrarse" },
  home: {
    heroBadge: "De confianza en 120+ países",
    heroTitle: "Viaja ligero.\nSigue en línea.",
    heroSub: "Un QR. Cero roaming. En línea antes del despegue.",
    cta: "Encontrar mi plan", ctaSecondary: "Cómo funciona",
    statPlans: "planes de datos", statCountries: "países",
    statActivation: "para conectarte", statSupport: "soporte humano",
    popularEyebrow: "Favoritos de viajeros",
    popularTitle: "¿Adónde te llevan tus próximos pasos?",
    popularSub: "Desde escapadas de una semana hasta un viaje por 35 países, elige el plan que siga tu ruta.",
    seeAll: "Ver todos los destinos",
    howEyebrow: "Cómo funciona",
    howTitle: "Tres toques. Estás conectado.",
    step1Title: "Elige tu viaje",
    step1Desc: "Selecciona un país o región y el paquete de datos que encaje con tu estancia.",
    step2Title: "Pago rápido y seguro",
    step2Desc: "Pago con tarjeta gestionado por Stripe. No necesitas cuenta para comprar — checkout de invitado en segundos.",
    step3Title: "Escanea. Listo.",
    step3Desc: "Tu QR y el enlace de instalación con un toque llegan por correo. Añade la eSIM y aterriza ya conectado.",
    trustEyebrow: "Por qué los viajeros eligen BlueRoam",
    trustTitle: "La forma más sencilla de seguir conectado en el extranjero.",
    trustBullet1: "Entrega inmediata — tu eSIM llega al correo antes de embarcar.",
    trustBullet2: "Precios transparentes en euros. Ningún susto de roaming al volver a casa.",
    trustBullet3: "Funciona en cualquier móvil moderno: iPhone XS+, Pixel 3+, Galaxy S20+ y más.",
    faqEyebrow: "Respuestas rápidas",
    faqTitle: "Todo lo que querías preguntar.",
    faq1q: "¿Qué es una eSIM?",
    faq1a: "Una eSIM es una tarjeta SIM digital integrada en tu móvil. La activas con un QR — sin SIM física, sin cambiar, sin envíos.",
    faq2q: "¿Funcionará en mi móvil?",
    faq2a: "BlueRoam funciona en iPhone XS en adelante, Pixel 3 en adelante, Galaxy S20 en adelante y la mayoría de Android de gama alta. Si dudas, revisa Ajustes → Añadir eSIM.",
    faq3q: "¿Cuándo empieza mi plan?",
    faq3a: "La mayoría de planes se activan en el momento en que tu móvil se conecta a una red local. Puedes instalar la eSIM días antes del vuelo sin gastar datos.",
    faq4q: "¿Puedo conservar mi número habitual?",
    faq4a: "Sí. Tu eSIM de BlueRoam convive con tu SIM normal, así que sigues recibiendo llamadas y mensajes como siempre.",
    ctaBandTitle: "Tu viaje. En línea. En 90 segundos.",
    ctaBandSub: "500+ planes de datos en 120+ países. Entrega inmediata por correo.",
    heroLivePill: "viajeros conectándose ahora",
    heroLocalEs: "Planes para España — ver precios",
    explainEyebrow: "Cómo funciona BlueRoam",
    explainTitle: "Dos formas de estar conectado en el extranjero.",
    explainSub: "Elige un plan de datos para un viaje — o abre una cartera Pay & Fly que te acompaña por cada país.",
    explainOneTitle: "Planes por país",
    explainOneDesc: "¿Sabes a dónde vas? Toma un paquete fijo para ese país o región. Un precio, un QR, sin sorpresas.",
    explainOneBullet1: "120+ países y paquetes regionales (Europa, Asia, Global)",
    explainOneBullet2: "Desde 1 GB / 7 días hasta planes ilimitados de 30 días",
    explainOneBullet3: "Entregado por correo en segundos",
    explainOneCta: "Ver planes por país",
    explainTwoTitle: "Cartera Pay & Fly",
    explainTwoDesc: "Ideal para viajeros frecuentes y viajes multi-país. Recarga una vez, instala una eSIM y tu saldo se consume a tarifas locales.",
    explainTwoBullet1: "Una eSIM para los 120+ países — sin reinstalar",
    explainTwoBullet2: "Paga solo los datos que usas, con tarifas claras por GB",
    explainTwoBullet3: "12 meses de validez · recarga auto opcional",
    explainTwoCta: "Verlo en acción",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "Una cartera. Cada frontera. Paga por GB.",
    payflyBandDescShort: "Recarga, instala una eSIM, y cargamos a tarifas locales. Prueba la demo →",
    payflyStep1TitleShort: "Recargar",
    payflyStep2TitleShort: "Instalar eSIM",
    payflyStep3TitleShort: "Viajar y usar",
    walletCtaGo: "Abrir mi cartera",
    walletCtaCreateShort: "Crear mi cartera",
    walletCtaLoginShort: "Iniciar sesión",
    finalEyebrow: "¿Listo?",
  },
  plans: { title: "Encuentra tu plan", subtitle: "Precios justos. Velocidades locales. Entregado en segundos.", all: "Todos los destinos", empty: "No hay planes para esa búsqueda. Prueba con otro país.", regions: "Regiones" },
  checkout: {
    title: "Estás a punto de conectarte",
    planSummary: "Resumen del plan", totalDue: "Total",
    emailLabel: "Correo para tu QR", emailHelp: "Enviaremos la eSIM y el enlace de instalación a esta dirección.",
    terms: "Pago seguro con Stripe · Las tarjetas se procesan en EUR.",
    payNow: "Pagar y recibir mi eSIM", processing: "Redirigiendo a Stripe…",
    successTitle: "Pago recibido", successSub: "Estamos generando tu eSIM. Tarda entre 5 y 20 segundos.",
    waiting: "Generando tu eSIM…",
    esimReady: "Tu eSIM está lista",
    scanQR: "En tu móvil abre Ajustes → Datos móviles → Añadir eSIM y escanea este código.",
    iosInstall: "Instalar en iPhone (con un toque)", passport: "Abrir Pasaporte eSIM",
    trouble: "¿Algún problema? Tu eSIM está guardada en tu panel — puedes instalarla cuando quieras.",
    iccid: "ICCID",
    failedTitle: "Seguimos con ello",
    failedSub: "El pago se realizó, pero la provisión está reintentando. Recarga en un momento o contacta con soporte — lo solucionamos.",
    copyLPA: "Copiar código de activación", copied: "Copiado",
  },
  dash: {
    title: "Mis eSIMs", subtitle: "Todos tus planes, uso y pedidos en un solo sitio.",
    activeEsims: "eSIMs activas", recentOrders: "Pedidos recientes",
    noEsims: "Aún no tienes eSIMs — consigue una para tu próximo viaje.",
    browse: "Buscar un plan", dataUsed: "usado", dataLeft: "restante",
    expires: "Caduca", viewDetails: "Ver detalles", orderId: "Pedido", amount: "Importe",
  },
  esim: { details: "Detalles de la eSIM", installation: "Instalación", usage: "Consumo", qrLabel: "QR de activación" },
  auth: {
    loginTitle: "Bienvenido de nuevo", loginSub: "Inicia sesión para gestionar tus viajes y eSIMs.",
    noAccount: "¿Nuevo en BlueRoam?", createOne: "Crea una cuenta",
    registerTitle: "Crea tu cuenta BlueRoam", registerSub: "Ten tus eSIMs en un solo lugar y viaja aún más ligero la próxima vez.",
    haveAccount: "¿Ya tienes cuenta?", loginHere: "Inicia sesión",
    invalid: "El correo y la contraseña no coinciden.",
  },
  admin: {
    title: "Operaciones", subtitle: "Back office", stats: "Resumen",
    orders: "Pedidos", customers: "Clientes", esims: "eSIMs", catalog: "Catálogo",
    syncPlans: "Sincronizar catálogo", synced: "Catálogo sincronizado",
    markup: "Margen", manualPrice: "Precio manual", wholesale: "Precio mayorista", retail: "Precio final",
    featured: "Destacado", enabled: "Activo",
    totalOrders: "Pedidos", fulfilled: "Completados", failed: "Fallidos", revenue: "Ingresos (EUR)",
    customersStat: "Clientes", activePlans: "Planes activos", esimsIssued: "eSIMs emitidas",
    retryProv: "Reintentar", retrySent: "Reintento en cola",
    customer: "Cliente", orderCount: "Pedidos", esimCount: "eSIMs",
  },
  footer: {
    tagline: "Tu conexión de bolsillo con el mundo.",
    product: "Producto", company: "Empresa", legal: "Legal",
    terms: "Términos", privacy: "Privacidad", cookies: "Cookies",
    usPrivacy: "Aviso de privacidad EE. UU.",
    refunds: "Política de reembolso",
    amlkyc: "Política AML / KYC",
    cookiePrefs: "Preferencias de cookies",
    cookieBanner: {
      title: "Usamos cookies para que BlueRoam funcione correctamente.",
      body: "Las cookies estrictamente necesarias siempre están activas. Las cookies de análisis y marketing están desactivadas hasta que las permita. Puede cambiarlo en cualquier momento desde el pie de página.",
      learnMore: "Aviso de cookies",
      preferences: "Preferencias",
      reject: "Rechazar no esenciales",
      accept: "Aceptar todo",
      save: "Guardar preferencias",
      prefsTitle: "Preferencias de cookies",
      prefsIntro: "Elija qué categorías de cookies puede colocar BlueRoam en su dispositivo. Modificable en cualquier momento.",
      gpcTitle: "Global Privacy Control detectado.",
      gpcBody: "Respetamos su señal GPC y hemos desactivado las cookies de análisis y marketing. Puede cambiarlo aquí si lo desea — solo afecta a este navegador.",
      catNec: "Estrictamente necesarias",
      catNecDesc: "Necesarias para inicio de sesión, pago, idioma y seguridad. No se pueden desactivar.",
      catAna: "Análisis",
      catAnaDesc: "Datos de uso agregados para mejorar el sitio. Las direcciones IP se truncan.",
      catMkt: "Marketing",
      catMktDesc: "Mensajes personalizados y publicidad de contexto cruzado. Desactivadas por defecto. No vendemos datos personales.",
    },
    doNotSell: "No vender ni compartir mi información personal",
    limitSensitive: "Limitar el uso de mi información personal sensible",
    websiteTerms: "Condiciones del sitio",
    support: "Soporte",
    about: "Sobre nosotros", contact: "Contacto", status: "Estado",
    copyright: "© 2026 BlueRoam. Todos los derechos reservados.",
  },
};

const el = {
  common: {
    brand: "BlueRoam", tagline: "Ταξιδέψτε ελαφριά. Μείνετε online.",
    searchPlaceholder: "Πού πηγαίνετε;",
    buy: "Επιλογή πακέτου", continue: "Συνέχεια", back: "Πίσω",
    email: "Email", password: "Κωδικός", name: "Όνομα",
    login: "Σύνδεση", register: "Δημιουργία λογαριασμού", logout: "Αποσύνδεση",
    dashboard: "Οι eSIM μου", admin: "Διαχείριση", loading: "Φόρτωση…",
    error: "Κάτι πήγε στραβά", price: "Τιμή", from: "από",
    data: "Δεδομένα", days: "Ημέρες", country: "Χώρα", plans: "πακέτα",
    cancel: "Ακύρωση", save: "Αποθήκευση", retry: "Νέα προσπάθεια",
    status: "Κατάσταση", actions: "Ενέργειες", language: "Γλώσσα", close: "Κλείσιμο",
    welcome: "Καλώς ήρθατε", destination: "Προορισμός", yourEmail: "Το email σας",
    payWith: "Πληρωμή με κάρτα", unlimited: "Απεριόριστα", moreCountries: "ακόμη χώρες", bubbleReady: "eSIM ΕΤΟΙΜΗ", bubbleConnected: "Συνδεδεμένος στο Παρίσι", bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 GB · 30 ημέρες", topup: "Ανανέωση / Επαναφόρτιση", topupTitle: "Ανανέωση αυτής της eSIM", topupSub: "Επιλέξτε νέο πακέτο — τα δεδομένα προστίθενται στην ίδια eSIM", sales: "Πωλήσεις", export: "Εξαγωγή CSV", chartRevenue: "Έσοδα", chartOrders: "Παραγγελίες", last30Days: "Τελευταίες 30 ημέρες", topCountries: "Κορυφαίες χώρες", topPlans: "Κορυφαία πακέτα", aov: "Μ.Ο. παραγγελίας", conversion: "Μετατροπή", fulfillment: "Ποσοστό ολοκλήρωσης", newCustomers: "Νέοι πελάτες", topupCount: "Ανανεώσεις",
  },
  nav: { home: "Αρχική", plans: "Προορισμοί", howItWorks: "Πώς λειτουργεί", support: "Βοήθεια", login: "Σύνδεση", signup: "Εγγραφή" },
  home: {
    heroBadge: "Αξιόπιστο σε 120+ χώρες",
    heroTitle: "Ταξιδέψτε ελαφριά.\nΜείνετε online.",
    heroSub: "Ένα QR code. Μηδέν χρεώσεις περιαγωγής. Online πριν την απογείωση.",
    cta: "Βρείτε το πακέτο μου", ctaSecondary: "Πώς λειτουργεί",
    statPlans: "πακέτα δεδομένων", statCountries: "χώρες",
    statActivation: "για σύνδεση", statSupport: "πραγματική υποστήριξη",
    popularEyebrow: "Αγαπημένα των ταξιδιωτών",
    popularTitle: "Πού θα σας πάνε τα επόμενα βήματά σας;",
    popularSub: "Από σύντομες αποδράσεις έως road trip σε 35 χώρες, επιλέξτε το πακέτο που ταιριάζει στη διαδρομή σας.",
    seeAll: "Όλοι οι προορισμοί",
    howEyebrow: "Πώς λειτουργεί",
    howTitle: "Τρία πατήματα. Συνδεθήκατε.",
    step1Title: "Επιλέξτε το ταξίδι σας",
    step1Desc: "Διαλέξτε χώρα ή περιοχή και το πακέτο δεδομένων που ταιριάζει στη διαμονή σας.",
    step2Title: "Γρήγορη και ασφαλής πληρωμή",
    step2Desc: "Πληρωμή με κάρτα μέσω Stripe. Δεν χρειάζεται λογαριασμός — checkout ως επισκέπτης σε δευτερόλεπτα.",
    step3Title: "Σαρώστε. Είστε online.",
    step3Desc: "Ο κωδικός QR και ο σύνδεσμος εγκατάστασης έρχονται με email. Προσθέστε την eSIM και προσγειωθείτε ήδη συνδεδεμένοι.",
    trustEyebrow: "Γιατί οι ταξιδιώτες επιλέγουν τη BlueRoam",
    trustTitle: "Ο πιο απλός τρόπος να μένετε online στο εξωτερικό.",
    trustBullet1: "Άμεση παράδοση — η eSIM φτάνει στο inbox σας πριν απογειωθείτε.",
    trustBullet2: "Διάφανες τιμές σε ευρώ. Καμία έκπληξη από περιαγωγή όταν επιστρέψετε.",
    trustBullet3: "Λειτουργεί σε κάθε σύγχρονο κινητό: iPhone XS+, Pixel 3+, Galaxy S20+ και άλλα.",
    faqEyebrow: "Γρήγορες απαντήσεις",
    faqTitle: "Ό,τι θέλατε να ρωτήσετε.",
    faq1q: "Τι είναι η eSIM;",
    faq1a: "Η eSIM είναι μια ψηφιακή κάρτα SIM ενσωματωμένη στο κινητό σας. Την ενεργοποιείτε με κωδικό QR — χωρίς πλαστική SIM, χωρίς αλλαγές, χωρίς αποστολές.",
    faq2q: "Θα λειτουργήσει στο κινητό μου;",
    faq2a: "Η BlueRoam λειτουργεί σε iPhone XS και νεότερα, Pixel 3 και νεότερα, Galaxy S20 και νεότερα και στα περισσότερα σύγχρονα Android. Αν δεν είστε σίγουροι, ελέγξτε Ρυθμίσεις → Προσθήκη eSIM.",
    faq3q: "Πότε ξεκινά το πακέτο μου;",
    faq3a: "Τα περισσότερα πακέτα ενεργοποιούνται μόλις το κινητό σας συνδεθεί σε τοπικό δίκτυο. Μπορείτε να εγκαταστήσετε την eSIM ημέρες πριν το ταξίδι χωρίς να ξοδέψετε δεδομένα.",
    faq4q: "Μπορώ να κρατήσω τον κανονικό μου αριθμό;",
    faq4a: "Ναι. Η eSIM BlueRoam λειτουργεί παράλληλα με τη συνηθισμένη SIM σας, οπότε συνεχίζετε να λαμβάνετε κλήσεις και μηνύματα κανονικά.",
    ctaBandTitle: "Το ταξίδι σας. Online. Σε 90 δευτερόλεπτα.",
    ctaBandSub: "500+ πακέτα δεδομένων σε 120+ χώρες. Άμεση παράδοση με email.",
    heroLivePill: "ταξιδιώτες συνδέονται τώρα",
    heroLocalEl: "Δες πακέτα Ελλάδας — από €5",
    explainEyebrow: "Πώς λειτουργεί το BlueRoam",
    explainTitle: "Δύο τρόποι να μένεις online στο εξωτερικό.",
    explainSub: "Διάλεξε πακέτο δεδομένων για ένα ταξίδι — ή άνοιξε πορτοφόλι Pay & Fly που σε ακολουθεί σε κάθε χώρα.",
    explainOneTitle: "Πακέτα ανά χώρα",
    explainOneDesc: "Ξέρεις πού πας; Πάρε σταθερό πακέτο για τη χώρα ή περιοχή σου. Μία τιμή, ένα QR, χωρίς εκπλήξεις.",
    explainOneBullet1: "120+ χώρες και περιφερειακά πακέτα (Ευρώπη, Ασία, Παγκόσμιο)",
    explainOneBullet2: "Από 1 GB / 7 ημέρες έως απεριόριστα 30ήμερα",
    explainOneBullet3: "Παραδίδεται με email σε δευτερόλεπτα",
    explainOneCta: "Δες πακέτα ανά χώρα",
    explainTwoTitle: "Πορτοφόλι Pay & Fly",
    explainTwoDesc: "Ιδανικό για συχνούς ταξιδιώτες και ταξίδια σε πολλές χώρες. Φόρτωσέ το μία φορά, βάλε μία eSIM, και το υπόλοιπο χρεώνεται σε τοπικές τιμές.",
    explainTwoBullet1: "Μία eSIM για όλες τις 120+ χώρες — χωρίς επανεγκατάσταση",
    explainTwoBullet2: "Πληρώνεις μόνο τα GB που χρησιμοποιείς, με διαφανείς χρεώσεις",
    explainTwoBullet3: "12 μήνες ισχύς · προαιρετική αυτόματη ανανέωση",
    explainTwoCta: "Δες το σε δράση",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "Ένα πορτοφόλι. Κάθε σύνορο. Πλήρωσε ανά GB.",
    payflyBandDescShort: "Φόρτωσε, βάλε μία eSIM, χρεώνουμε σε τοπικές τιμές. Δοκίμασε το demo →",
    payflyStep1TitleShort: "Φόρτωση",
    payflyStep2TitleShort: "Εγκατάσταση eSIM",
    payflyStep3TitleShort: "Πέτα & χρησιμοποίησε",
    walletCtaGo: "Άνοιγμα πορτοφολιού",
    walletCtaCreateShort: "Φτιάξε πορτοφόλι",
    walletCtaLoginShort: "Σύνδεση",
    finalEyebrow: "Έτοιμος;",
  },
  plans: { title: "Βρείτε το πακέτο σας", subtitle: "Δίκαιες τιμές. Τοπικές ταχύτητες. Παράδοση σε δευτερόλεπτα.", all: "Όλοι οι προορισμοί", empty: "Κανένα πακέτο για αυτή την αναζήτηση. Δοκιμάστε άλλη χώρα.", regions: "Περιοχές" },
  checkout: {
    title: "Σχεδόν συνδεθήκατε",
    planSummary: "Σύνοψη πακέτου", totalDue: "Σύνολο",
    emailLabel: "Email για τον κωδικό QR σας", emailHelp: "Θα στείλουμε την eSIM και τον σύνδεσμο εγκατάστασης σε αυτή τη διεύθυνση.",
    terms: "Ασφαλής πληρωμή μέσω Stripe · Οι κάρτες χρεώνονται σε ευρώ.",
    payNow: "Πληρωμή & λήψη eSIM", processing: "Μεταφορά στο Stripe…",
    successTitle: "Η πληρωμή έγινε δεκτή", successSub: "Δημιουργούμε την eSIM σας τώρα. Συνήθως διαρκεί 5 με 20 δευτερόλεπτα.",
    waiting: "Δημιουργία eSIM…",
    esimReady: "Η eSIM σας είναι έτοιμη",
    scanQR: "Στο κινητό σας ανοίξτε Ρυθμίσεις → Κινητή τηλεφωνία → Προσθήκη eSIM και σαρώστε αυτόν τον κωδικό.",
    iosInstall: "Εγκατάσταση σε iPhone (με ένα πάτημα)", passport: "Άνοιγμα Διαβατηρίου eSIM",
    trouble: "Πρόβλημα; Η eSIM σας είναι αποθηκευμένη στον πίνακά σας — μπορείτε να την εγκαταστήσετε οποιαδήποτε στιγμή.",
    iccid: "ICCID",
    failedTitle: "Συνεχίζουμε να προσπαθούμε",
    failedSub: "Η πληρωμή ολοκληρώθηκε, αλλά η έκδοση κάνει νέα προσπάθεια. Ανανεώστε σε λίγο ή επικοινωνήστε με την υποστήριξη — θα το φτιάξουμε.",
    copyLPA: "Αντιγραφή κωδικού ενεργοποίησης", copied: "Αντιγράφηκε",
  },
  dash: {
    title: "Οι eSIM μου", subtitle: "Όλα τα πακέτα, η χρήση και οι παραγγελίες σας σε ένα μέρος.",
    activeEsims: "Ενεργές eSIM", recentOrders: "Πρόσφατες παραγγελίες",
    noEsims: "Δεν έχετε eSIM ακόμη — πάρτε μία για το επόμενο ταξίδι σας.",
    browse: "Βρείτε πακέτο", dataUsed: "χρησιμοποιήθηκαν", dataLeft: "απομένουν",
    expires: "Λήγει", viewDetails: "Λεπτομέρειες", orderId: "Παραγγελία", amount: "Ποσό",
  },
  esim: { details: "Λεπτομέρειες eSIM", installation: "Εγκατάσταση", usage: "Χρήση", qrLabel: "Κωδικός QR ενεργοποίησης" },
  auth: {
    loginTitle: "Καλώς ήρθατε πάλι", loginSub: "Συνδεθείτε για να διαχειριστείτε ταξίδια και eSIM.",
    noAccount: "Νέος στη BlueRoam;", createOne: "Δημιουργία λογαριασμού",
    registerTitle: "Δημιουργήστε τον λογαριασμό BlueRoam", registerSub: "Κρατήστε όλες τις eSIM σας σε ένα μέρος και ταξιδέψτε ακόμη πιο ελαφριά.",
    haveAccount: "Έχετε ήδη λογαριασμό;", loginHere: "Σύνδεση",
    invalid: "Το email και ο κωδικός δεν ταιριάζουν.",
  },
  admin: {
    title: "Λειτουργία", subtitle: "Back office", stats: "Επισκόπηση",
    orders: "Παραγγελίες", customers: "Πελάτες", esims: "eSIMs", catalog: "Κατάλογος",
    syncPlans: "Συγχρονισμός καταλόγου", synced: "Κατάλογος συγχρονίστηκε",
    markup: "Περιθώριο", manualPrice: "Χειροκίνητη τιμή", wholesale: "Τιμή χονδρικής", retail: "Τελική τιμή",
    featured: "Προτεινόμενο", enabled: "Ενεργό",
    totalOrders: "Παραγγελίες", fulfilled: "Ολοκληρωμένες", failed: "Απέτυχαν", revenue: "Έσοδα (EUR)",
    customersStat: "Πελάτες", activePlans: "Ενεργά πακέτα", esimsIssued: "Εκδοθείσες eSIM",
    retryProv: "Νέα προσπάθεια", retrySent: "Σε ουρά",
    customer: "Πελάτης", orderCount: "Παραγγελίες", esimCount: "eSIMs",
  },
  footer: {
    tagline: "Η σύνδεσή σας με τον κόσμο, στην τσέπη σας.",
    product: "Προϊόν", company: "Εταιρεία", legal: "Νομικά",
    terms: "Όροι", privacy: "Απόρρητο", cookies: "Cookies",
    usPrivacy: "Σημείωση απορρήτου ΗΠΑ",
    refunds: "Πολιτική επιστροφών",
    amlkyc: "Πολιτική AML / KYC",
    cookiePrefs: "Προτιμήσεις cookies",
    cookieBanner: {
      title: "Χρησιμοποιούμε cookies για να λειτουργεί ομαλά το BlueRoam.",
      body: "Τα αυστηρώς απαραίτητα cookies είναι πάντα ενεργά. Τα cookies ανάλυσης και marketing είναι ανενεργά μέχρι να τα επιτρέψετε. Μπορείτε να αλλάξετε γνώμη οποτεδήποτε από το υποσέλιδο.",
      learnMore: "Σημείωση cookies",
      preferences: "Προτιμήσεις",
      reject: "Απόρριψη μη απαραίτητων",
      accept: "Αποδοχή όλων",
      save: "Αποθήκευση",
      prefsTitle: "Προτιμήσεις cookies",
      prefsIntro: "Επιλέξτε ποιες κατηγορίες cookies μπορεί να τοποθετήσει το BlueRoam στη συσκευή σας.",
      gpcTitle: "Εντοπίστηκε Global Privacy Control.",
      gpcBody: "Σεβαστήκαμε το σήμα GPC σας και απενεργοποιήσαμε τα cookies ανάλυσης και marketing. Μπορείτε να το αλλάξετε εδώ — επηρεάζει μόνο αυτόν τον περιηγητή.",
      catNec: "Αυστηρώς απαραίτητα",
      catNecDesc: "Απαραίτητα για σύνδεση, ταμείο, γλώσσα και ασφάλεια. Δεν απενεργοποιούνται.",
      catAna: "Ανάλυση",
      catAnaDesc: "Συγκεντρωτικά δεδομένα χρήσης για βελτίωση του ιστότοπου. Οι διευθύνσεις IP περικόπτονται.",
      catMkt: "Marketing",
      catMktDesc: "Εξατομικευμένα μηνύματα και διασταυρούμενη διαφήμιση. Ανενεργά εξ ορισμού. Δεν πωλούμε προσωπικά δεδομένα.",
    },
    doNotSell: "Μην πουλάτε ή μοιράζεστε τα προσωπικά μου δεδομένα",
    limitSensitive: "Περιορισμός χρήσης ευαίσθητων προσωπικών δεδομένων",
    websiteTerms: "Όροι ιστότοπου",
    support: "Υποστήριξη",
    about: "Σχετικά", contact: "Επικοινωνία", status: "Κατάσταση",
    copyright: "© 2026 BlueRoam. Όλα τα δικαιώματα διατηρούνται.",
  },
};

const ar = {
  common: {
    brand: "BlueRoam", tagline: "سافر بخفّة. ابقَ متصلًا.",
    searchPlaceholder: "إلى أين تسافر؟",
    buy: "اختر هذه الباقة", continue: "متابعة", back: "رجوع",
    email: "البريد الإلكتروني", password: "كلمة المرور", name: "الاسم",
    login: "تسجيل الدخول", register: "إنشاء حساب", logout: "تسجيل الخروج",
    dashboard: "بطاقات eSIM الخاصة بي", admin: "الإدارة", loading: "جارٍ التحميل…",
    error: "حدث خطأ ما", price: "السعر", from: "ابتداءً من",
    data: "بيانات", days: "أيام", country: "الدولة", plans: "باقات",
    cancel: "إلغاء", save: "حفظ", retry: "إعادة المحاولة",
    status: "الحالة", actions: "إجراءات", language: "اللغة", close: "إغلاق",
    welcome: "أهلًا بك", destination: "الوجهة", yourEmail: "بريدك الإلكتروني",
    payWith: "ادفع بالبطاقة", unlimited: "غير محدود", moreCountries: "دولة أخرى",
    bubbleReady: "eSIM جاهزة", bubbleConnected: "متصل في باريس",
    bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 جيجا · 30 يومًا",
    topup: "تجديد / شحن", topupTitle: "تجديد هذه الـ eSIM",
    topupSub: "اختر باقة جديدة — تتراكم بياناتك على نفس الـ eSIM",
    sales: "المبيعات", export: "تصدير CSV",
    chartRevenue: "الإيرادات", chartOrders: "الطلبات",
    last30Days: "آخر 30 يومًا", topCountries: "أفضل الدول", topPlans: "أفضل الباقات",
    aov: "متوسط الطلب", conversion: "التحويل", fulfillment: "معدل الإنجاز",
    newCustomers: "عملاء جدد", topupCount: "تجديدات",
  },
  nav: { home: "الرئيسية", plans: "الوجهات", howItWorks: "كيف يعمل", support: "الدعم", login: "دخول", signup: "اشتراك" },
  home: {
    heroBadge: "موثوق في أكثر من 120 دولة",
    heroTitle: "سافر بخفّة.\nابقَ متصلًا.",
    heroSub: "رمز QR واحد. بدون رسوم تجوال. متصل قبل الإقلاع.",
    cta: "ابحث عن باقتي", ctaSecondary: "كيف يعمل",
    statPlans: "باقة بيانات", statCountries: "دولة", statActivation: "للاتصال", statSupport: "دعم بشري",
    popularEyebrow: "مفضّلات المسافرين",
    popularTitle: "إلى أين ستأخذك خطواتك القادمة؟",
    popularSub: "من رحلات أسبوع واحد إلى جولات تشمل 35 دولة، اختر الباقة التي تناسب رحلتك.",
    seeAll: "كل الوجهات",
    howEyebrow: "كيف يعمل", howTitle: "ثلاث نقرات. وأنت متصل.",
    step1Title: "اختر رحلتك", step1Desc: "اختر الدولة أو المنطقة وحجم البيانات المناسب لإقامتك.",
    step2Title: "ادفع بسرعة وأمان", step2Desc: "الدفع بالبطاقة عبر Stripe. لا حاجة لحساب — الدفع كزائر في ثوانٍ.",
    step3Title: "امسح. أنت متصل.", step3Desc: "يصل رمز QR ورابط التثبيت إلى بريدك. أضف الـ eSIM واهبط متصلًا.",
    trustEyebrow: "لماذا يختار المسافرون BlueRoam", trustTitle: "أبسط طريقة للبقاء متصلًا في الخارج.",
    trustBullet1: "تسليم فوري — تصل بطاقة eSIM إلى بريدك قبل إقلاع طائرتك.",
    trustBullet2: "أسعار شفافة باليورو. لا مفاجآت تجوال عند العودة.",
    trustBullet3: "تعمل على جميع الهواتف الحديثة: iPhone XS+، Pixel 3+، Galaxy S20+ وأكثر.",
    faqEyebrow: "إجابات سريعة", faqTitle: "كل ما أردت معرفته.",
    faq1q: "ما هي بطاقة eSIM؟",
    faq1a: "بطاقة eSIM هي بطاقة SIM رقمية مدمجة في هاتفك. تفعّلها برمز QR — لا بطاقة بلاستيكية، لا تبديل، لا شحن.",
    faq2q: "هل ستعمل على هاتفي؟",
    faq2a: "BlueRoam تعمل على iPhone XS وأحدث، Pixel 3 وأحدث، Galaxy S20 وأحدث، ومعظم هواتف Android الرائدة. عند الشك، تحقق من الإعدادات ← إضافة eSIM.",
    faq3q: "متى تبدأ باقتي؟",
    faq3a: "تنشط معظم الباقات لحظة اتصال هاتفك بشبكة محلية. يمكنك تثبيت الـ eSIM قبل أيام من السفر دون استهلاك بيانات.",
    faq4q: "هل يمكنني الاحتفاظ برقمي العادي؟",
    faq4a: "نعم. تعمل eSIM BlueRoam جنبًا إلى جنب مع شريحتك العادية، فتستمر في تلقي المكالمات والرسائل.",
    ctaBandTitle: "رحلتك. متصلًا. في 90 ثانية.",
    ctaBandSub: "أكثر من 500 باقة بيانات في أكثر من 120 دولة. تسليم فوري عبر البريد.",
    heroLivePill: "مسافر يتصلون الآن",
    heroLocalAr: "تصفح باقات المغرب",
    explainEyebrow: "كيف يعمل BlueRoam",
    explainTitle: "طريقتان للبقاء متصلًا بالخارج.",
    explainSub: "اختر باقة بيانات لرحلة واحدة — أو افتح محفظة Pay & Fly تتبعك في كل بلد.",
    explainOneTitle: "باقات الدول",
    explainOneDesc: "تعرف وجهتك؟ احصل على باقة ثابتة لذلك البلد أو المنطقة. سعر واحد، رمز QR واحد، بلا مفاجآت.",
    explainOneBullet1: "أكثر من 120 دولة وباقات إقليمية (أوروبا، آسيا، عالمي)",
    explainOneBullet2: "من 1 جيجا / 7 أيام حتى باقات 30 يومًا غير محدودة",
    explainOneBullet3: "يصل عبر البريد الإلكتروني في ثوانٍ",
    explainOneCta: "تصفح الباقات",
    explainTwoTitle: "محفظة Pay & Fly",
    explainTwoDesc: "مثالية للمسافرين الدائمين ورحلات عدة دول. اشحن مرة واحدة، ثبّت eSIM واحدة، ورصيدك يُخصم بأسعار محلية.",
    explainTwoBullet1: "eSIM واحدة لجميع 120+ دولة — بلا إعادة تثبيت",
    explainTwoBullet2: "ادفع فقط مقابل ما تستخدمه بأسعار شفافة لكل جيجا",
    explainTwoBullet3: "صلاحية 12 شهرًا · شحن تلقائي اختياري",
    explainTwoCta: "شاهدها تعمل",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "محفظة واحدة. كل الحدود. ادفع لكل جيجا.",
    payflyBandDescShort: "اشحن، ثبّت eSIM، نخصم بأسعار محلية أثناء السفر. جرّب العرض →",
    payflyStep1TitleShort: "اشحن",
    payflyStep2TitleShort: "ثبّت eSIM",
    payflyStep3TitleShort: "سافر واستخدم",
    walletCtaGo: "افتح محفظتي",
    walletCtaCreateShort: "ابدأ محفظتي",
    walletCtaLoginShort: "تسجيل الدخول",
    finalEyebrow: "جاهز؟",
  },
  plans: { title: "اعثر على باقتك", subtitle: "أسعار عادلة. سرعات محلية. تسليم في ثوانٍ.", all: "كل الوجهات", empty: "لا توجد باقات لهذا البحث. جرّب دولة أخرى.", regions: "المناطق" },
  checkout: {
    title: "أنت على وشك الاتصال", planSummary: "ملخص الباقة", totalDue: "الإجمالي",
    emailLabel: "بريد لاستلام رمز QR", emailHelp: "سنرسل eSIM ورابط الإعداد إلى هذا العنوان.",
    terms: "دفع آمن عبر Stripe · البطاقات تُعالَج باليورو.",
    payNow: "ادفع واحصل على eSIM", processing: "جارٍ التحويل إلى Stripe…",
    successTitle: "تم استلام الدفعة", successSub: "نُجهّز بطاقتك الآن. عادةً تستغرق من 5 إلى 20 ثانية.",
    waiting: "جارٍ إنشاء eSIM…", esimReady: "eSIM جاهزة للمسح",
    scanQR: "افتح في هاتفك الإعدادات ← بيانات الجوال ← إضافة eSIM وامسح هذا الرمز.",
    iosInstall: "ثبّت على iPhone (بنقرة)", passport: "افتح جواز سفر eSIM",
    trouble: "هناك مشكلة؟ بطاقتك محفوظة في لوحتك — يمكنك تثبيتها في أي وقت.",
    iccid: "ICCID",
    failedTitle: "ما زلنا نعمل عليها", failedSub: "تمّ الدفع، لكن التحضير يُعيد المحاولة. حدّث الصفحة بعد قليل أو تواصل مع الدعم.",
    copyLPA: "نسخ كود التفعيل", copied: "تم النسخ",
  },
  dash: {
    title: "بطاقات eSIM الخاصة بي", subtitle: "كل باقاتك واستهلاكك وطلباتك في مكان واحد.",
    activeEsims: "بطاقات eSIM نشطة", recentOrders: "الطلبات الأخيرة",
    noEsims: "لا تملك بطاقة eSIM بعد — احصل على واحدة لرحلتك القادمة.",
    browse: "ابحث عن باقة", dataUsed: "مُستهلك", dataLeft: "متبقي",
    expires: "ينتهي في", viewDetails: "عرض التفاصيل", orderId: "الطلب", amount: "المبلغ",
  },
  esim: { details: "تفاصيل eSIM", installation: "التثبيت", usage: "الاستهلاك", qrLabel: "رمز QR للتفعيل" },
  auth: {
    loginTitle: "أهلًا بعودتك", loginSub: "سجّل الدخول لإدارة رحلاتك وبطاقات eSIM.",
    noAccount: "جديد على BlueRoam؟", createOne: "أنشئ حسابًا",
    registerTitle: "أنشئ حساب BlueRoam", registerSub: "احتفظ بكل بطاقاتك في مكان واحد وسافر بخفّة في المرة القادمة.",
    haveAccount: "لديك حساب؟", loginHere: "سجّل الدخول",
    invalid: "البريد وكلمة المرور غير متطابقين.",
  },
  admin: {
    title: "العمليات", subtitle: "الإدارة", stats: "نظرة عامة",
    orders: "الطلبات", customers: "العملاء", esims: "eSIMs", catalog: "الكتالوج",
    syncPlans: "مزامنة الكتالوج", synced: "تمت المزامنة",
    markup: "هامش الربح", manualPrice: "سعر يدوي", wholesale: "السعر بالجملة", retail: "سعر التجزئة",
    featured: "مميّز", enabled: "مفعّل",
    totalOrders: "الطلبات", fulfilled: "مكتملة", failed: "فاشلة", revenue: "الإيرادات (EUR)",
    customersStat: "العملاء", activePlans: "الباقات النشطة", esimsIssued: "بطاقات eSIM المُصدرة",
    retryProv: "أعِد المحاولة", retrySent: "في قائمة الإعادة",
    customer: "العميل", orderCount: "الطلبات", esimCount: "بطاقات eSIM",
  },
  footer: {
    tagline: "اتصالك بالعالم في جيبك.",
    product: "المنتج", company: "الشركة", legal: "قانوني",
    terms: "الشروط", privacy: "الخصوصية", cookies: "ملفات تعريف الارتباط",
    usPrivacy: "إشعار خصوصية الولايات المتحدة",
    refunds: "سياسة الاسترداد",
    amlkyc: "سياسة AML / KYC",
    cookiePrefs: "تفضيلات ملفات تعريف الارتباط",
    cookieBanner: {
      title: "نستخدم ملفات تعريف الارتباط لتشغيل BlueRoam بسلاسة.",
      body: "ملفات تعريف الارتباط الضرورية تعمل دائمًا. ملفات التحليل والتسويق معطلة حتى تسمح بها. يمكنك التغيير في أي وقت من تذييل الصفحة.",
      learnMore: "إشعار ملفات تعريف الارتباط",
      preferences: "التفضيلات",
      reject: "رفض غير الضرورية",
      accept: "قبول الكل",
      save: "حفظ التفضيلات",
      prefsTitle: "تفضيلات ملفات تعريف الارتباط",
      prefsIntro: "اختر فئات ملفات تعريف الارتباط التي يمكن لـ BlueRoam وضعها على جهازك.",
      gpcTitle: "تم اكتشاف Global Privacy Control.",
      gpcBody: "نحترم إشارة GPC الخاصة بك وقمنا بإيقاف تشغيل ملفات تعريف الارتباط للتحليل والتسويق. يمكنك تجاوز ذلك هنا — يؤثر فقط على هذا المتصفح.",
      catNec: "ضرورية بشكل صارم",
      catNecDesc: "مطلوبة لتسجيل الدخول والدفع وتفضيل اللغة والأمان. لا يمكن تعطيلها.",
      catAna: "التحليل",
      catAnaDesc: "بيانات استخدام مجمعة لتحسين الموقع. يتم اقتطاع عناوين IP.",
      catMkt: "التسويق",
      catMktDesc: "رسائل مخصصة وإعلانات عبر السياق. معطلة افتراضيًا. نحن لا نبيع بيانات شخصية.",
    },
    doNotSell: "لا تبيعوا أو تشاركوا معلوماتي الشخصية",
    limitSensitive: "تقييد استخدام معلوماتي الشخصية الحساسة",
    websiteTerms: "شروط الموقع",
    support: "الدعم",
    about: "من نحن", contact: "تواصل معنا", status: "الحالة",
    copyright: "© 2026 BlueRoam. جميع الحقوق محفوظة.",
  },
};

const zh = {
  common: {
    brand: "BlueRoam", tagline: "轻装出行，时刻在线。",
    searchPlaceholder: "你要去哪里？",
    buy: "选择此套餐", continue: "继续", back: "返回",
    email: "邮箱", password: "密码", name: "姓名",
    login: "登录", register: "创建账户", logout: "退出",
    dashboard: "我的 eSIM", admin: "管理", loading: "加载中…",
    error: "出错了", price: "价格", from: "起",
    data: "流量", days: "天数", country: "国家", plans: "套餐",
    cancel: "取消", save: "保存", retry: "重试",
    status: "状态", actions: "操作", language: "语言", close: "关闭",
    welcome: "欢迎", destination: "目的地", yourEmail: "你的邮箱",
    payWith: "刷卡支付", unlimited: "无限", moreCountries: "个国家",
    bubbleReady: "eSIM 已就绪", bubbleConnected: "已在巴黎连接",
    bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 GB · 30 天",
    topup: "续订 / 充值", topupTitle: "续订此 eSIM",
    topupSub: "选择新套餐 — 流量会叠加到同一张 eSIM",
    sales: "销售", export: "导出 CSV",
    chartRevenue: "收入", chartOrders: "订单",
    last30Days: "最近 30 天", topCountries: "热门国家", topPlans: "热门套餐",
    aov: "平均订单", conversion: "转化率", fulfillment: "完成率",
    newCustomers: "新客户", topupCount: "续订数",
  },
  nav: { home: "首页", plans: "目的地", howItWorks: "工作原理", support: "帮助", login: "登录", signup: "注册" },
  home: {
    heroBadge: "覆盖 120+ 国家",
    heroTitle: "轻装出行。\n时刻在线。",
    heroSub: "一个二维码。零漫游费。起飞前已在线。",
    cta: "找到我的套餐", ctaSecondary: "工作原理",
    statPlans: "数据套餐", statCountries: "个国家", statActivation: "即可连接", statSupport: "真人客服",
    popularEyebrow: "旅行者最爱",
    popularTitle: "你的下一站去哪里？",
    popularSub: "从一周短途到 35 国跨境之旅，选择适合你行程的套餐。",
    seeAll: "查看全部目的地",
    howEyebrow: "工作原理", howTitle: "三步搞定，立即在线。",
    step1Title: "选择行程", step1Desc: "选择国家或区域以及适合你停留时长的流量包。",
    step2Title: "快速安全付款", step2Desc: "由 Stripe 处理的银行卡支付。无需账户 — 几秒钟完成访客结账。",
    step3Title: "扫码，即刻在线。", step3Desc: "二维码和一键安装链接通过邮件送达。添加 eSIM，落地即连接。",
    trustEyebrow: "为什么旅行者选择 BlueRoam", trustTitle: "在境外保持连接最简单的方式。",
    trustBullet1: "即时交付 — 你的 eSIM 在登机前就到达邮箱。",
    trustBullet2: "以欧元定价，价格透明。回家不再有意外漫游费。",
    trustBullet3: "兼容所有现代手机：iPhone XS+、Pixel 3+、Galaxy S20+ 等。",
    faqEyebrow: "常见问题", faqTitle: "你想问的都在这里。",
    faq1q: "什么是 eSIM？",
    faq1a: "eSIM 是内置在手机里的数字 SIM 卡。通过二维码激活 — 无实体卡、无需更换、无需邮寄。",
    faq2q: "我的手机能用吗？",
    faq2a: "BlueRoam 支持 iPhone XS 及更新、Pixel 3 及更新、Galaxy S20 及更新，以及大多数旗舰 Android。不确定时请查看设置 → 添加 eSIM。",
    faq3q: "套餐什么时候开始？",
    faq3a: "大多数套餐在手机连接到本地网络时立即激活。可以在出发前几天安装 eSIM 而不消耗流量。",
    faq4q: "可以保留我原来的号码吗？",
    faq4a: "可以。BlueRoam eSIM 与原 SIM 并行运行，你可以继续接听电话和接收信息。",
    ctaBandTitle: "你的旅行。在线。90 秒。",
    ctaBandSub: "120+ 个国家、500+ 套餐。邮件即时送达。",
    heroLivePill: "位旅客正在连接",
    heroLocalZh: "查看中国套餐",
    explainEyebrow: "BlueRoam 如何运作",
    explainTitle: "两种方式，海外时刻在线。",
    explainSub: "为单次旅程挑选数据套餐 — 或开通 Pay & Fly 钱包，跨越每一个国家。",
    explainOneTitle: "国家套餐",
    explainOneDesc: "目的地明确？为该国家或地区获取固定流量包。一个价格，一个 QR，无任何惊喜。",
    explainOneBullet1: "120+ 国家与区域套餐（欧洲、亚洲、全球）",
    explainOneBullet2: "从 1 GB / 7 天到 30 天无限量",
    explainOneBullet3: "付款后数秒邮件送达",
    explainOneCta: "浏览国家套餐",
    explainTwoTitle: "Pay & Fly 钱包",
    explainTwoDesc: "适合常旅客和多国行程。一次充值，安装一张 eSIM，余额按当地资费扣除。",
    explainTwoBullet1: "一张 eSIM 覆盖 120+ 国家 — 无需重新安装",
    explainTwoBullet2: "只为实际使用的数据付费，按 GB 透明计价",
    explainTwoBullet3: "12 个月有效期 · 可选自动充值",
    explainTwoCta: "看它如何运作",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "一个钱包。跨越边境。按 GB 付费。",
    payflyBandDescShort: "充值、安装一张 eSIM，漫游时按当地资费扣款。试用演示 →",
    payflyStep1TitleShort: "充值",
    payflyStep2TitleShort: "安装 eSIM",
    payflyStep3TitleShort: "出行使用",
    walletCtaGo: "打开钱包",
    walletCtaCreateShort: "开通钱包",
    walletCtaLoginShort: "登录",
    finalEyebrow: "准备好了？",
  },
  plans: { title: "找到你的套餐", subtitle: "公平价格。本地速度。秒级送达。", all: "全部目的地", empty: "未找到匹配的套餐。请尝试其他国家。", regions: "区域" },
  checkout: {
    title: "即将连接", planSummary: "套餐摘要", totalDue: "合计",
    emailLabel: "接收二维码的邮箱", emailHelp: "我们会将 eSIM 和安装链接发送到此地址。",
    terms: "Stripe 安全支付 · 银行卡以欧元结算。",
    payNow: "支付并获取 eSIM", processing: "正在跳转到 Stripe…",
    successTitle: "已收到付款", successSub: "正在为你生成 eSIM，通常 5 到 20 秒。",
    waiting: "正在生成 eSIM…", esimReady: "你的 eSIM 已就绪",
    scanQR: "在手机上打开 设置 → 蜂窝网络 → 添加 eSIM 并扫描此码。",
    iosInstall: "在 iPhone 上安装（一键）", passport: "打开 eSIM 护照",
    trouble: "遇到问题？你的 eSIM 已安全保存在仪表板，可以随时安装。",
    iccid: "ICCID",
    failedTitle: "我们仍在处理", failedSub: "支付成功，但配置正在重试。请稍后刷新或联系客服 — 我们会处理好。",
    copyLPA: "复制激活码", copied: "已复制",
  },
  dash: {
    title: "我的 eSIM", subtitle: "你的所有套餐、用量和订单都在一处。",
    activeEsims: "活动 eSIM", recentOrders: "最近订单",
    noEsims: "你还没有 eSIM — 为下次旅行准备一张吧。",
    browse: "找套餐", dataUsed: "已用", dataLeft: "剩余",
    expires: "到期", viewDetails: "查看详情", orderId: "订单", amount: "金额",
  },
  esim: { details: "eSIM 详情", installation: "安装", usage: "用量", qrLabel: "激活二维码" },
  auth: {
    loginTitle: "欢迎回来", loginSub: "登录以管理你的旅行和 eSIM。",
    noAccount: "BlueRoam 新用户？", createOne: "创建账户",
    registerTitle: "创建你的 BlueRoam 账户", registerSub: "把所有 eSIM 集中管理，下次旅行更轻松。",
    haveAccount: "已有账户？", loginHere: "登录",
    invalid: "邮箱和密码不匹配。",
  },
  admin: {
    title: "运营", subtitle: "后台", stats: "概览",
    orders: "订单", customers: "客户", esims: "eSIMs", catalog: "目录",
    syncPlans: "同步目录", synced: "目录已同步",
    markup: "加价", manualPrice: "手动价格", wholesale: "批发价", retail: "零售价",
    featured: "精选", enabled: "启用",
    totalOrders: "订单数", fulfilled: "已完成", failed: "失败", revenue: "收入 (EUR)",
    customersStat: "客户", activePlans: "在售套餐", esimsIssued: "已发 eSIM",
    retryProv: "重试", retrySent: "已加入重试",
    customer: "客户", orderCount: "订单", esimCount: "eSIMs",
  },
  footer: {
    tagline: "你与世界的口袋连接。",
    product: "产品", company: "公司", legal: "法律",
    terms: "条款", privacy: "隐私", cookies: "Cookies",
    usPrivacy: "美国隐私声明",
    refunds: "退款政策",
    amlkyc: "AML / KYC 政策",
    cookiePrefs: "Cookie 偏好",
    cookieBanner: {
      title: "我们使用 Cookie 让 BlueRoam 顺畅运行。",
      body: "严格必要的 Cookie 始终开启。分析和营销 Cookie 在您允许之前是关闭的。您可以随时在页脚更改。",
      learnMore: "Cookie 声明",
      preferences: "偏好",
      reject: "拒绝非必要",
      accept: "全部接受",
      save: "保存偏好",
      prefsTitle: "Cookie 偏好",
      prefsIntro: "选择 BlueRoam 可以在您的设备上设置哪些类别的 Cookie。",
      gpcTitle: "已检测到 Global Privacy Control。",
      gpcBody: "我们尊重您的 GPC 信号,已关闭分析和营销 Cookie。您可以在此覆盖该设置,但仅影响此浏览器。",
      catNec: "严格必要",
      catNecDesc: "登录、结账、语言偏好和安全所必需。无法禁用。",
      catAna: "分析",
      catAnaDesc: "聚合使用数据以改进网站。IP 地址被截断。",
      catMkt: "营销",
      catMktDesc: "个性化消息和跨上下文广告。默认关闭。我们不出售个人信息。",
    },
    doNotSell: "请勿出售或共享我的个人信息",
    limitSensitive: "限制使用我的敏感个人信息",
    websiteTerms: "网站条款",
    support: "支持",
    about: "关于", contact: "联系", status: "状态",
    copyright: "© 2026 BlueRoam. 保留所有权利。",
  },
};

const ja = {
  common: {
    brand: "BlueRoam", tagline: "身軽に旅して、ずっとオンライン。",
    searchPlaceholder: "どこへ行きますか？",
    buy: "このプランを選ぶ", continue: "続ける", back: "戻る",
    email: "メール", password: "パスワード", name: "名前",
    login: "ログイン", register: "アカウント作成", logout: "ログアウト",
    dashboard: "マイ eSIM", admin: "管理", loading: "読み込み中…",
    error: "問題が発生しました", price: "料金", from: "から",
    data: "データ", days: "日数", country: "国", plans: "プラン",
    cancel: "キャンセル", save: "保存", retry: "もう一度",
    status: "ステータス", actions: "操作", language: "言語", close: "閉じる",
    welcome: "ようこそ", destination: "目的地", yourEmail: "メールアドレス",
    payWith: "カードで支払う", unlimited: "無制限", moreCountries: "ヶ国以上",
    bubbleReady: "eSIM 準備完了", bubbleConnected: "パリで接続中",
    bubbleNetwork: "Orange FR · 5G", bubblePlanShort: "+5 GB · 30日",
    topup: "更新 / チャージ", topupTitle: "この eSIM を更新",
    topupSub: "新しいプランを選ぶ — データは同じ eSIM に追加されます",
    sales: "売上", export: "CSVエクスポート",
    chartRevenue: "売上", chartOrders: "注文数",
    last30Days: "過去30日", topCountries: "人気の国", topPlans: "人気プラン",
    aov: "平均注文額", conversion: "コンバージョン", fulfillment: "履行率",
    newCustomers: "新規顧客", topupCount: "更新数",
  },
  nav: { home: "ホーム", plans: "目的地", howItWorks: "仕組み", support: "ヘルプ", login: "ログイン", signup: "登録" },
  home: {
    heroBadge: "120ヶ国以上で利用可能",
    heroTitle: "身軽に旅して。\nずっとオンライン。",
    heroSub: "ひとつのQR。ローミング料金ゼロ。離陸前からオンライン。",
    cta: "プランを探す", ctaSecondary: "仕組み",
    statPlans: "データプラン", statCountries: "ヶ国", statActivation: "で接続", statSupport: "サポート",
    popularEyebrow: "旅行者のお気に入り",
    popularTitle: "次の旅はどこへ？",
    popularSub: "1週間の旅行から35ヶ国のロードトリップまで、あなたの旅程に合うプランを選びましょう。",
    seeAll: "すべての目的地",
    howEyebrow: "仕組み", howTitle: "3タップで接続完了。",
    step1Title: "旅を選ぶ", step1Desc: "国または地域と滞在に合うデータ容量を選択。",
    step2Title: "素早く安全な支払い", step2Desc: "Stripeによるカード決済。アカウント不要 — ゲストチェックアウトで数秒。",
    step3Title: "スキャンして接続。", step3Desc: "QRコードとワンタップ設定リンクをメールでお届け。eSIMを追加して、到着と同時にオンライン。",
    trustEyebrow: "BlueRoam が選ばれる理由", trustTitle: "海外でつながる、最もシンプルな方法。",
    trustBullet1: "即時配信 — 搭乗前に eSIM が受信箱に届きます。",
    trustBullet2: "ユーロ建てで透明な価格。帰国後にローミング料金で驚くことはありません。",
    trustBullet3: "あらゆるモダンなスマホで動作: iPhone XS+、Pixel 3+、Galaxy S20+ など。",
    faqEyebrow: "よくある質問", faqTitle: "知りたいことすべて。",
    faq1q: "eSIM とは？",
    faq1a: "eSIM はスマホ内蔵のデジタルSIMカード。QRコードで有効化 — 物理SIMも交換も配送も不要です。",
    faq2q: "私のスマホで動作しますか？",
    faq2a: "BlueRoam は iPhone XS 以降、Pixel 3 以降、Galaxy S20 以降、および主要な Android で動作します。不明な場合は 設定 → eSIMを追加 を確認してください。",
    faq3q: "プランはいつ開始しますか？",
    faq3a: "ほとんどのプランは現地ネットワークに接続した瞬間に有効化されます。出発の数日前に eSIM をインストールしてもデータは消費されません。",
    faq4q: "通常の番号は維持できますか？",
    faq4a: "はい。BlueRoam eSIM は通常の SIM と並行して動作し、通話やメッセージは変わらず受信できます。",
    ctaBandTitle: "あなたの旅。オンライン。90秒で。",
    ctaBandSub: "120ヶ国以上で500以上のデータプラン。メールで即時配信。",
    heroLivePill: "人の旅行者が接続中",
    heroLocalJa: "日本プランを見る — スキャンして出発",
    explainEyebrow: "BlueRoam の仕組み",
    explainTitle: "海外でつながる2つの方法。",
    explainSub: "1回の旅行用にデータプランを選ぶ — または Pay & Fly ウォレットで、どの国でも継続利用。",
    explainOneTitle: "国別プラン",
    explainOneDesc: "行き先が決まっているなら、その国・地域の固定データパックを。ワンプライス、ワンQR、迷いなし。",
    explainOneBullet1: "120+ カ国と地域パック（欧州・アジア・グローバル）",
    explainOneBullet2: "1 GB / 7日〜無制限30日プランまで",
    explainOneBullet3: "お支払い後、数秒でメール配信",
    explainOneCta: "国別プランを見る",
    explainTwoTitle: "Pay & Fly ウォレット",
    explainTwoDesc: "よく旅する方や複数国の旅行に最適。一度チャージして一枚の eSIM を使えば、現地料金で残高から引き落とし。",
    explainTwoBullet1: "120+ カ国すべてを1枚の eSIM で — 再インストール不要",
    explainTwoBullet2: "実際に使ったデータだけ、GB単位の透明料金",
    explainTwoBullet3: "12ヶ月有効 · 任意の自動チャージ",
    explainTwoCta: "実際に試す",
    payflyBandEyebrow: "Pay & Fly",
    payflyBandTitle: "ひとつのウォレット。国境を越え、GBごとに支払う。",
    payflyBandDescShort: "チャージして eSIM を入れ、現地料金で残高から。デモを試す →",
    payflyStep1TitleShort: "チャージ",
    payflyStep2TitleShort: "eSIM 設定",
    payflyStep3TitleShort: "飛んで使う",
    walletCtaGo: "ウォレットを開く",
    walletCtaCreateShort: "ウォレットを作成",
    walletCtaLoginShort: "ログイン",
    finalEyebrow: "準備OK？",
  },
  plans: { title: "プランを探す", subtitle: "公正な価格。現地速度。数秒で配信。", all: "すべての目的地", empty: "条件に合うプランがありません。別の国をお試しください。", regions: "地域" },
  checkout: {
    title: "もうすぐ接続です", planSummary: "プラン概要", totalDue: "合計",
    emailLabel: "QRコード受信用メール", emailHelp: "このアドレスに eSIM と設定リンクをお送りします。",
    terms: "Stripe による安全な決済 · カードはユーロで処理されます。",
    payNow: "支払って eSIM を取得", processing: "Stripe にリダイレクト中…",
    successTitle: "お支払いを受け付けました", successSub: "eSIM を作成しています。通常5〜20秒かかります。",
    waiting: "eSIM 生成中…", esimReady: "eSIM の準備ができました",
    scanQR: "スマホで 設定 → モバイル通信 → eSIM追加 を開き、このコードをスキャンしてください。",
    iosInstall: "iPhone にインストール（ワンタップ）", passport: "eSIMパスポートを開く",
    trouble: "問題ですか？eSIM はダッシュボードに保存されているので、いつでもインストールできます。",
    iccid: "ICCID",
    failedTitle: "現在処理中です", failedSub: "支払いは完了しましたが、プロビジョニングが再試行中です。少し待ってから更新するか、サポートにお問い合わせください。",
    copyLPA: "アクティベーションコードをコピー", copied: "コピーしました",
  },
  dash: {
    title: "マイ eSIM", subtitle: "プラン、使用量、注文をすべて1か所で。",
    activeEsims: "有効な eSIM", recentOrders: "最近の注文",
    noEsims: "まだ eSIM をお持ちではありません — 次の旅行のために手に入れましょう。",
    browse: "プランを探す", dataUsed: "使用済み", dataLeft: "残り",
    expires: "有効期限", viewDetails: "詳細を見る", orderId: "注文", amount: "金額",
  },
  esim: { details: "eSIM 詳細", installation: "インストール", usage: "使用量", qrLabel: "アクティベーション QR" },
  auth: {
    loginTitle: "おかえりなさい", loginSub: "旅と eSIM を管理するためにログインしてください。",
    noAccount: "BlueRoam は初めてですか？", createOne: "アカウントを作成",
    registerTitle: "BlueRoam アカウントを作成", registerSub: "eSIM を1か所にまとめて、次の旅をもっと身軽に。",
    haveAccount: "アカウントをお持ちですか？", loginHere: "ログイン",
    invalid: "メールとパスワードが一致しません。",
  },
  admin: {
    title: "オペレーション", subtitle: "バックオフィス", stats: "概要",
    orders: "注文", customers: "顧客", esims: "eSIM", catalog: "カタログ",
    syncPlans: "カタログ同期", synced: "同期完了",
    markup: "マークアップ", manualPrice: "手動価格", wholesale: "卸価格", retail: "小売価格",
    featured: "おすすめ", enabled: "有効",
    totalOrders: "注文数", fulfilled: "完了", failed: "失敗", revenue: "売上 (EUR)",
    customersStat: "顧客", activePlans: "アクティブなプラン", esimsIssued: "発行済 eSIM",
    retryProv: "再試行", retrySent: "再試行待ち",
    customer: "顧客", orderCount: "注文", esimCount: "eSIM",
  },
  footer: {
    tagline: "ポケットの中の世界とのつながり。",
    product: "製品", company: "会社", legal: "法的事項",
    terms: "利用規約", privacy: "プライバシー", cookies: "Cookies",
    usPrivacy: "米国プライバシー通知",
    refunds: "返金ポリシー",
    amlkyc: "AML / KYC ポリシー",
    cookiePrefs: "Cookie 設定",
    cookieBanner: {
      title: "BlueRoam を快適に動かすために Cookie を使用しています。",
      body: "必須 Cookie は常に有効です。分析・マーケティング Cookie はあなたが許可するまでオフです。フッターからいつでも変更できます。",
      learnMore: "Cookie 通知",
      preferences: "設定",
      reject: "必須以外を拒否",
      accept: "すべて許可",
      save: "設定を保存",
      prefsTitle: "Cookie 設定",
      prefsIntro: "BlueRoam がデバイスに設定できる Cookie のカテゴリを選んでください。",
      gpcTitle: "Global Privacy Control を検出しました。",
      gpcBody: "GPC シグナルを尊重し、分析・マーケティング Cookie をオフにしました。ここで上書きできます — このブラウザにのみ適用されます。",
      catNec: "厳密に必要",
      catNecDesc: "ログイン・チェックアウト・言語設定・セキュリティに必須。無効化できません。",
      catAna: "分析",
      catAnaDesc: "サイト改善のための集計データ。IP アドレスは切り詰めます。",
      catMkt: "マーケティング",
      catMktDesc: "パーソナライズ通知とクロスコンテキスト広告。既定でオフ。個人情報の販売は行いません。",
    },
    doNotSell: "個人情報の販売・共有を停止",
    limitSensitive: "機微な個人情報の利用を制限",
    websiteTerms: "ウェブサイト規約",
    support: "サポート",
    about: "概要", contact: "お問い合わせ", status: "ステータス",
    copyright: "© 2026 BlueRoam. All rights reserved.",
  },
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    de: { translation: de },
    es: { translation: es },
    el: { translation: el },
    ar: { translation: ar },
    zh: { translation: zh },
    ja: { translation: ja },
  },
  lng: (() => {
    const saved = localStorage.getItem("lang");
    // If a user previously had Turkish selected, fall back to English so the
    // language switcher doesn't show a blank label.
    return saved && saved !== "tr" ? saved : "en";
  })(),
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

export const RTL_LANGS = ["ar"];
export const LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "es", label: "ES", name: "Español" },
  { code: "el", label: "EL", name: "Ελληνικά" },
  { code: "ar", label: "AR", name: "العربية" },
  { code: "zh", label: "ZH", name: "中文" },
  { code: "ja", label: "JA", name: "日本語" },
];

export default i18n;
